/**
 * B2B B2B Checkout Page
 * Handles checkout form interactions and cart data display
 */
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

const GADGET_API_BASE = getCustomerAppBaseUrl();

/** US state full name -> 2-digit code for converting Shopify province data */
const US_STATE_NAME_TO_CODE = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

class B2BCheckout {
  constructor() {
    this.cart = null;
    this.b2bContext = null;
    this.personalAddresses = [];
    this.selectedAddressBookAddress = null;
    /** Customer metafield customer.attentions (JSON): address GID -> attention string for address book */
    this.customerAttentions = {};
    this.shippingRates = [];
    this.carrierGroups = {};
    this.strings =
      typeof window.B2B_CHECKOUT_CONFIG !== "undefined" &&
      window.B2B_CHECKOUT_CONFIG.strings
        ? window.B2B_CHECKOUT_CONFIG.strings
        : {};
    if (typeof history !== "undefined" && "scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    this.init();
  }

  /**
   * Build window.B2B_CHECKOUT_STATES from Shopify's hidden `all_country_option_tags` select,
   * keyed by ISO code to match `#new-country` option values (same list as address book).
   */
  buildStatesFromShopify() {
    const hidden = document.getElementById("b2b-country-provinces-data");
    if (!hidden) return;

    const provincesByCountryName = {};
    for (const opt of hidden.options) {
      try {
        const raw = opt.getAttribute("data-provinces");
        if (!raw) continue;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) continue;
        provincesByCountryName[opt.value] = arr.map((pair) => {
          const label = pair[0] ?? "";
          const rawValue = pair[1] ?? pair[0] ?? "";
          return { label, value: rawValue };
        });
      } catch (_) {
        // ignore parse errors
      }
    }

    const normalize = (s) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    /**
     * Match `#new-country` option text to Shopify hidden `option.value` keys (see `all_country_option_tags`).
     * Checkout option labels in Liquid are kept aligned with those values; we also allow a normalized
     * containment match when one string is a clear superset of the other (e.g. longer official title).
     */
    const findProvinces = (displayLabel) => {
      const dl = displayLabel?.trim() || "";
      if (dl && provincesByCountryName[dl]?.length) {
        return provincesByCountryName[dl];
      }
      const nTarget = normalize(dl);
      if (!nTarget) return [];
      for (const [name, provinces] of Object.entries(provincesByCountryName)) {
        if (provinces?.length && normalize(name) === nTarget) {
          return provinces;
        }
      }
      let best = null;
      let bestKeyLen = 0;
      for (const [name, provinces] of Object.entries(provincesByCountryName)) {
        if (!provinces?.length) continue;
        const nk = normalize(name);
        if (!nk) continue;
        const shorter = nk.length <= nTarget.length ? nk : nTarget;
        const longer = nk.length > nTarget.length ? nk : nTarget;
        if (shorter.length < 5) continue;
        if (longer.includes(shorter) && nk.length >= bestKeyLen) {
          best = provinces;
          bestKeyLen = nk.length;
        }
      }
      return best || [];
    };

    const countrySelect = document.getElementById("new-country");
    const states = {};
    if (countrySelect) {
      for (const isoOpt of countrySelect.options) {
        const iso = isoOpt.value?.trim();
        if (!iso) continue;
        const displayLabel = isoOpt.textContent?.trim() || "";
        let list = findProvinces(displayLabel);
        if (iso === "US") {
          list = list.map(({ label, value: rawValue }) => {
            const v =
              String(rawValue).length > 2
                ? US_STATE_NAME_TO_CODE[rawValue] || rawValue
                : rawValue;
            return { label, value: v };
          });
        }
        states[iso] = list;
      }
    } else {
      let usList = findProvinces("United States");
      usList = usList.map(({ label, value: rawValue }) => {
        const v =
          String(rawValue).length > 2
            ? US_STATE_NAME_TO_CODE[rawValue] || rawValue
            : rawValue;
        return { label, value: v };
      });
      states.US = usList;
      states.CA = findProvinces("Canada");
    }

    window.B2B_CHECKOUT_STATES = states;
  }

  /**
   * True when Shopify provides subdivisions for this country (state/province required).
   * @param {string} countryCode
   * @returns {boolean}
   */
  checkoutCountryHasStateOptions(countryCode) {
    const code = (countryCode || "").trim().toUpperCase();
    const list = window.B2B_CHECKOUT_STATES?.[code];
    return Array.isArray(list) && list.length > 0;
  }

  /**
   * Convert state/province to 2-digit code. Handles full names (e.g. "California") for US states.
   * @param {string} stateOrProvince - Full name or 2-digit code
   * @param {string} countryCode - US or CA
   * @returns {string} 2-digit state/province code
   */
  toStateCode(stateOrProvince, countryCode = "US") {
    if (!stateOrProvince || typeof stateOrProvince !== "string") return "";
    const s = stateOrProvince.trim();
    if (!s) return "";
    if (s.length === 2) return s.toUpperCase();
    if (countryCode === "US") {
      const key = Object.keys(US_STATE_NAME_TO_CODE).find(
        (k) => k.toLowerCase() === s.toLowerCase()
      );
      return key ? US_STATE_NAME_TO_CODE[key] : s;
    }
    return s;
  }

  /**
   * Active storefront currency (matches Liquid `B2B_CHECKOUT_CONFIG.currency` / `cart.currency.iso_code`).
   * @returns {string}
   */
  getCheckoutCurrency() {
    const fromConfig =
      typeof window.B2B_CHECKOUT_CONFIG !== "undefined" &&
      window.B2B_CHECKOUT_CONFIG.currency;
    if (fromConfig) return String(fromConfig).toUpperCase();
    const fromCart = this.cart?.currency;
    if (typeof fromCart === "string" && fromCart.trim() !== "") {
      return fromCart.toUpperCase();
    }
    return "USD";
  }

  /**
   * Locale for Intl currency formatting (theme `<html lang>`).
   * @returns {string}
   */
  getMoneyFormatLocale() {
    const lang =
      typeof document !== "undefined" &&
      document.documentElement?.getAttribute("lang");
    return lang && lang.trim() !== "" ? lang.trim() : "en-US";
  }

  async init() {
    window.scrollTo(0, 0);
    this.buildStatesFromShopify();
    if (
      window.B2B_CHECKOUT_ATTENTIONS &&
      typeof window.B2B_CHECKOUT_ATTENTIONS === "object"
    ) {
      this.customerAttentions = { ...window.B2B_CHECKOUT_ATTENTIONS };
    }
    await this.fetchCart();
    await this.fetchB2BContext();
    await this.fetchPersonalAddresses();
    this.render();
    this.bindEvents();
    this.observeB2bSummaryCurrency();
    await this.populateFromB2BContext();
    // Fetch shipping conditions from API and populate carrier/service dropdowns
    await this.fetchShippingConditions();
    this.groupRatesByCarrier();
    this.populateCarrierDropdown();
    this.restoreCheckoutFieldsFromCart();
    this.restoreShippingMethodFromCart();
    this.initInputFloatLabels();
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }

  /**
   * Keep `.b2b-checkout__input-float-wrap--filled` in sync so labels stay floated when inputs have values
   * (including programmatic fills and browser autofill).
   */
  syncInputFloatLabels() {
    document.querySelectorAll(".b2b-checkout__input-float-wrap").forEach((wrap) => {
      const input = wrap.querySelector(".b2b-checkout__input--has-float-label");
      if (!input) return;
      wrap.classList.toggle(
        "b2b-checkout__input-float-wrap--filled",
        input.value.trim() !== ""
      );
    });
  }

  /**
   * Keep `.b2b-checkout__select-float-wrap--filled` in sync for native selects (country/state).
   */
  syncSelectFloatLabels() {
    document
      .querySelectorAll(".b2b-checkout__select-float-wrap")
      .forEach((wrap) => {
        const select = wrap.querySelector(
          "select.b2b-checkout__select--has-float-label",
        );
        if (!select) return;
        wrap.classList.toggle(
          "b2b-checkout__select-float-wrap--filled",
          select.value.trim() !== "",
        );
      });
  }

  initInputFloatLabels() {
    const root = document.querySelector(".b2b-checkout");
    if (!root) return;
    const onValueChange = (e) => {
      const t = e.target;
      if (t?.classList?.contains("b2b-checkout__input--has-float-label")) {
        const wrap = t.closest(".b2b-checkout__input-float-wrap");
        if (wrap) {
          wrap.classList.toggle(
            "b2b-checkout__input-float-wrap--filled",
            t.value.trim() !== "",
          );
        }
      }
      if (t?.matches?.("select.b2b-checkout__select--has-float-label")) {
        const wrap = t.closest(".b2b-checkout__select-float-wrap");
        if (wrap) {
          wrap.classList.toggle(
            "b2b-checkout__select-float-wrap--filled",
            t.value.trim() !== "",
          );
        }
      }
    };
    root.addEventListener("input", onValueChange);
    root.addEventListener("change", onValueChange);
    root.addEventListener("blur", onValueChange, true);
    this.syncInputFloatLabels();
    this.syncSelectFloatLabels();
    this.bindNativeSelectOpenFloatLabels(root);
  }

  /**
   * Float labels while a native select is active or its picker is open. Some browsers do not keep
   * :focus-within on the wrapper while the system dropdown is open; toggling --select-open fixes that.
   */
  bindNativeSelectOpenFloatLabels(root) {
    const isFloatSelect = (el) =>
      el?.matches?.("select.b2b-checkout__select--has-float-label");

    const setSelectOpen = (select, open) => {
      const wrap = select?.closest?.(".b2b-checkout__select-float-wrap");
      if (!wrap || !isFloatSelect(select)) return;
      wrap.classList.toggle(
        "b2b-checkout__select-float-wrap--select-open",
        open,
      );
    };

    root.addEventListener(
      "pointerdown",
      (e) => {
        if (isFloatSelect(e.target)) setSelectOpen(e.target, true);
      },
      true,
    );
    root.addEventListener(
      "keydown",
      (e) => {
        if (!isFloatSelect(e.target)) return;
        const k = e.key;
        if (
          k === "ArrowDown" ||
          k === "ArrowUp" ||
          k === " " ||
          k === "Enter"
        ) {
          setSelectOpen(e.target, true);
        }
      },
      true,
    );
    root.addEventListener(
      "focusin",
      (e) => {
        if (isFloatSelect(e.target)) setSelectOpen(e.target, true);
      },
      true,
    );
    root.addEventListener(
      "focusout",
      (e) => {
        if (!isFloatSelect(e.target)) return;
        const select = e.target;
        requestAnimationFrame(() => {
          if (document.activeElement === select) return;
          setSelectOpen(select, false);
        });
      },
      true,
    );
    root.addEventListener(
      "change",
      (e) => {
        if (isFloatSelect(e.target)) setSelectOpen(e.target, false);
      },
      true,
    );
  }

  /**
   * Reformat B2B summary elements from realtime pricing (e.g. "4,131.00 USD") to localized currency display.
   */
  formatB2bSummaryCurrency(el) {
    if (!el?.hasAttribute("data-cart-total-price")) return;
    const text = String(el.textContent || "").trim();
    const code = this.getCheckoutCurrency();
    if (!text || !text.toUpperCase().includes(code)) return;
    const num = parseFloat(text.replace(/[^\d.]/g, ""));
    if (Number.isNaN(num)) return;
    el.textContent = new Intl.NumberFormat(this.getMoneyFormatLocale(), {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).format(num);
  }

  /**
   * Observe summary subtotal/total so when realtime pricing sets "X,XXX.00 {currency}" we reformat with Intl.
   */
  observeB2bSummaryCurrency() {
    const subtotalEl = document.getElementById("summary-subtotal");
    const totalEl = document.getElementById("summary-total");
    const elements = [subtotalEl, totalEl].filter(Boolean);
    if (elements.length === 0) return;

    const formatAll = () => {
      elements.forEach((el) => this.formatB2bSummaryCurrency(el));
      const totalText = totalEl?.textContent?.trim() || "";
      if (totalText) {
        document.querySelectorAll(".js-summary-total").forEach((el) => {
          if (el !== totalEl) el.textContent = totalText;
        });
      }
    };

    const observer = new MutationObserver(() => formatAll());
    elements.forEach((el) => {
      observer.observe(el, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    });
    formatAll();
  }

  /**
   * Fetch shipping conditions from GET /api/checkout/shipping-conditions and set this.shippingRates.
   */
  async fetchShippingConditions() {
    const jwt = window.myJWT;
    if (!jwt) {
      this.shippingRates = [];
      return;
    }

    const url = `${GADGET_API_BASE}/api/checkout/shipping-conditions`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.status === 401) {
        this.shippingRates = [];
        return;
      }

      if (response.status === 404) {
        await response.json().catch(() => ({}));
        this.shippingRates = [];
        return;
      }

      if (response.status === 500) {
        this.shippingRates = [];
        return;
      }

      if (!response.ok) {
        this.shippingRates = [];
        return;
      }

      const data = await response.json();
      const carriers = data.carriers || [];
      this.shippingRates = [];
      for (const carrier of carriers) {
        if (carrier.active === "false") continue;
        const description = (carrier.description || "").trim();
        const carrierExternalId = carrier.vendor || "";
        const conditions = carrier.shipping_conditions || [];
        for (const condition of conditions) {
          if (condition.active === "false") continue;
          this.shippingRates.push({
            title:
              condition.shipping_method_name ||
              condition.shipping_condition_id ||
              "Shipping",
            handle: condition.shipping_condition_id || "",
            priceV2: {
              amount: "0",
              currencyCode: this.getCheckoutCurrency(),
            },
            carrierDescription: description,
            carrierExternalId,
            carrierCode:
              carrier.external_id ||
              carrier.carrier_id ||
              carrier.id ||
              carrierExternalId,
            shippingConditionCode: condition.shipping_condition_id || "",
            shippingConditionDescription:
              (condition.shipping_method_name || "").trim() ||
              condition.shipping_condition_id ||
              "",
            freight_terms: condition.freight_terms || [],
          });
        }
      }
    } catch (err) {
      this.shippingRates = [];
    }
  }

  /**
   * Parse context cookie (company_id, location_id) - same pattern as rs-current-company-location / rs-location-picker
   */
  getContextFromCookie() {
    const cookies = (document.cookie || "").split(";").reduce((acc, s) => {
      const i = s.indexOf("=");
      if (i === -1) return acc;
      acc[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(
        s.slice(i + 1).trim(),
      );
      return acc;
    }, {});
    try {
      return cookies.context ? JSON.parse(cookies.context) : null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch B2B context from GET /api/customer/companies (project API) and current context cookie.
   * Builds company name, location id, and location address from the companies/locations payload.
   */
  async fetchB2BContext() {
    if (!window.myJWT) {
      return;
    }

    try {
      const response = await fetch(
        `${GADGET_API_BASE}/api/customer/companies`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${window.myJWT}`,
          },
        },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const companies = data.companies || (Array.isArray(data) ? data : []);
      const ctx = this.getContextFromCookie();
      if (!ctx?.company_id || !companies.length) {
        return;
      }

      const company = companies.find(
        (c) => String(c.company_id) === String(ctx.company_id),
      );
      if (!company) return;

      const locationIdCookie = ctx.location_id;
      const locations = company.locations || [];
      const location = locations.find(
        (l) =>
          l.location_id === locationIdCookie ||
          (l.location_id &&
            l.location_id.replace("gid://shopify/CompanyLocation/", "") ===
              locationIdCookie),
      );

      this.b2bContext = {
        companyName:
          company.company_name || company.name || String(ctx.company_id),
        companyId: ctx.company_id,
        locationId: location?.location_id || locationIdCookie,
      };

      if (location?.shippingAddress) {
        const sa = location.shippingAddress;
        const rawProvince =
          sa.provinceCode || sa.province || sa.zoneCode || "";
        this.b2bContext.locationAddress = {
          address1: sa.address1 || "",
          address2: sa.address2 || "",
          city: sa.city || "",
          provinceCode:
            this.toStateCode(
              rawProvince,
              sa.countryCode || sa.country || ""
            ) || rawProvince,
          zip: sa.zip || "",
          countryCode: sa.countryCode || sa.country || "",
        };
      }
    } catch (error) {
      // B2B context fetch failed
    }
  }

  /**
   * Populate the checkout form with B2B context data (company name and address line)
   */
  populateFromB2BContext() {
    if (!this.b2bContext) return;

    const companyNameEl = document.getElementById("company-name");
    if (companyNameEl && this.b2bContext.companyName) {
      companyNameEl.textContent = this.b2bContext.companyName;
    }

    const addressLine = document.getElementById("address-line");
    if (addressLine && this.b2bContext.locationAddress) {
      const loc = this.b2bContext.locationAddress;
      const parts = [
        loc.address1,
        loc.city,
        loc.provinceCode || loc.province,
        loc.zip,
        loc.countryCode || loc.country,
      ].filter(Boolean);
      addressLine.textContent = parts.join(", ");

      // Add them as data attributes for the review order page
      const provinceCode =
        this.toStateCode(
          loc.provinceCode || loc.province,
          loc.countryCode || loc.country || ""
        ) || loc.provinceCode || loc.province;
      addressLine.setAttribute("data-address1", loc.address1);
      addressLine.setAttribute("data-city", loc.city);
      addressLine.setAttribute("data-provinceCode", provinceCode);
      addressLine.setAttribute("data-zip", loc.zip);
      addressLine.setAttribute("data-countryCode", loc.countryCode);
    }
  }

  /**
   * Get the current shipping address based on selection
   */
  getCurrentShippingAddress() {
    const shippingRadio = document.querySelector(
      'input[name="shipping_address"]:checked',
    );
    const addressType = shippingRadio?.value || "current";

    if (addressType === "new") {
      return {
        address1: document.getElementById("new-address-1")?.value || "",
        address2: document.getElementById("new-address-2")?.value || "",
        city: document.getElementById("new-city")?.value || "",
        provinceCode: document.getElementById("new-state")?.value || "",
        zip: document.getElementById("new-zip")?.value || "",
        countryCode: document.getElementById("new-country")?.value || "",
      };
    } else if (
      addressType === "addressbook" &&
      this.selectedAddressBookAddress
    ) {
      return {
        address1: this.selectedAddressBookAddress.address1 || "",
        address2: this.selectedAddressBookAddress.address2 || "",
        city: this.selectedAddressBookAddress.city || "",
        provinceCode: this.selectedAddressBookAddress.provinceCode || "",
        zip: this.selectedAddressBookAddress.zip || "",
        countryCode: this.selectedAddressBookAddress.countryCode || "",
      };
    } else {
      // Use B2B context location address
      if (this.b2bContext?.locationAddress) {
        const loc = this.b2bContext.locationAddress;
        return {
          address1: loc.address1 || "",
          address2: loc.address2 || "",
          city: loc.city || "",
          provinceCode: loc.provinceCode || loc.province || "",
          zip: loc.zip || "",
          countryCode: loc.countryCode || loc.country || "",
        };
      }
      // Fallback - use a default US address for initial load
      return {
        address1: "",
        city: "",
        provinceCode: "",
        zip: "",
        countryCode: "",
      };
    }
  }

  /**
   * Get the currently selected rate (carrier + service) or null
   */
  getSelectedRate() {
    const carrierValue = document
      .getElementById("carrier-select")
      ?.value?.trim();
    const serviceHandle = document
      .getElementById("service-select")
      ?.value?.trim();
    if (!carrierValue || !serviceHandle) return null;
    const carrierName = Object.keys(this.carrierGroups).find(
      (c) => c.toLowerCase().replace(/\s+/g, "-") === carrierValue,
    );
    if (!carrierName) return null;
    const rates = this.carrierGroups[carrierName] || [];
    return rates.find((r) => (r.handle || "") === serviceHandle) || null;
  }

  /**
   * Get the selected freight term code and description from a rate's freight_terms.
   * @param {Object|null} rate - Selected rate (from getSelectedRate)
   * @param {string} freightTermValue - Value from the freight term input (code or normalized name)
   * @returns {{ code: string, description: string }|null}
   */
  getSelectedFreightTerm(rate, freightTermValue) {
    if (!rate || !freightTermValue) return null;
    const terms = rate.freight_terms || [];
    const valueNorm = freightTermValue.toLowerCase().trim();
    const term = terms.find((t) => {
      const code = (t.freight_term || t.name || "").toString().trim();
      const codeNorm = code.toLowerCase().replace(/\s+/g, "-");
      return (
        codeNorm === valueNorm ||
        code.toLowerCase() === valueNorm ||
        (t.name || "").toString().toLowerCase().replace(/\s+/g, "-") ===
          valueNorm
      );
    });
    if (!term) return null;
    const code =
      term.freight_term || (term.name || "").toString().replace(/\s+/g, "-");
    const description = (term.description || term.name || code).toString();
    return { code, description };
  }

  /**
   * Populate and show/hide freight terms from the selected service rate.
   * If only one freight term: do not display radio buttons, persist freight term via hidden input,
   * and show Account Number field (only value available). If multiple terms: show radios; show
   * Account Number only when "bill to carrier account" is selected (not "add to my invoice").
   */
  populateFreightTerms(rate) {
    const container = document.getElementById("freight-terms-options");
    const field = document.getElementById("freight-terms-field");
    if (!container || !field) return;

    container.innerHTML = "";
    const terms = rate?.freight_terms || [];

    if (terms.length === 0) {
      field.style.display = "none";
      this.toggleCarrierDependentFields();
      return;
    }

    const singleTerm = terms[0];
    if (terms.length === 1) {
      const value =
        singleTerm.freight_term ||
        (singleTerm.name || "freight-0").toString().replace(/\s+/g, "-");
      container.innerHTML = `<input type="hidden" name="freight_term" value="${this.escapeHtml(value)}" data-single-freight-term="true">`;
      field.style.display = "none";
      this.toggleCarrierDependentFields();
      return;
    }

    const invoicePhrase = "add shipping charges to my invoice";
    const sortedTerms = [...terms].sort((a, b) => {
      const labelA = (a.description || a.name || "").toLowerCase();
      const labelB = (b.description || b.name || "").toLowerCase();
      if (labelA.includes(invoicePhrase) && !labelB.includes(invoicePhrase))
        return -1;
      if (!labelA.includes(invoicePhrase) && labelB.includes(invoicePhrase))
        return 1;
      return 0;
    });

    container.innerHTML = sortedTerms
      .map((term, i) => {
        const termId = (term.freight_term || term.name || `freight-${i}`)
          .toString()
          .replace(/\s+/g, "-");
        const label = term.description || term.name || termId;
        const value = term.freight_term || termId;
        const isInvoiceOption = (term.description || term.name || "")
          .toLowerCase()
          .includes(invoicePhrase);
        const dataAttr = isInvoiceOption ? ' data-invoice-option="true"' : "";
        return `
        <div class="b2b-checkout__radio-option">
          <input type="radio" name="freight_term" value="${this.escapeHtml(value)}" class="b2b-checkout__radio" id="freight-term-${i}"${dataAttr}${i === 0 ? " checked" : ""}>
          <div class="b2b-checkout__radio-content">
            <label for="freight-term-${i}" class="b2b-checkout__radio-label text-medium text-primary">${this.escapeHtml(label)}</label>
          </div>
        </div>
      `;
      })
      .join("");
    field.style.display = "block";
    this.toggleCarrierDependentFields();
  }

  /**
   * Group shipping rates by carrier name (uses API carrierDescription when present)
   */
  groupRatesByCarrier() {
    this.carrierGroups = {};

    this.shippingRates.forEach((rate) => {
      const carrierName =
        rate.carrierDescription != null && rate.carrierDescription !== ""
          ? rate.carrierDescription
          : this.extractCarrierName(rate);

      if (!this.carrierGroups[carrierName]) {
        this.carrierGroups[carrierName] = [];
      }
      this.carrierGroups[carrierName].push(rate);
    });
  }

  /**
   * Extract carrier name from shipping rate
   */
  extractCarrierName(rate) {
    const title = rate.title || "";
    const handle = rate.handle || "";

    // Common carrier patterns
    const carriers = [
      "FedEx",
      "UPS",
      "USPS",
      "DHL",
      "Canada Post",
      "Purolator",
    ];

    for (const carrier of carriers) {
      if (
        title.toLowerCase().includes(carrier.toLowerCase()) ||
        handle.toLowerCase().includes(carrier.toLowerCase())
      ) {
        return carrier;
      }
    }

    // Check for ERP-prefixed rates
    if (
      handle.includes("erp-") ||
      handle.includes("sap-") ||
      handle.includes("oracle-")
    ) {
      const parts = handle.split("-");
      if (parts.length > 1) {
        return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      }
    }

    // Default: use "Standard" for rates without clear carrier
    if (
      title.toLowerCase().includes("standard") ||
      title.toLowerCase().includes("economy") ||
      title.toLowerCase().includes("ground")
    ) {
      return "Standard";
    }

    if (
      title.toLowerCase().includes("express") ||
      title.toLowerCase().includes("priority") ||
      title.toLowerCase().includes("expedited")
    ) {
      return "Express";
    }

    // Fallback: use first word of title or "Other"
    const firstWord = title.split(" ")[0];
    return firstWord || "Other";
  }

  /**
   * Populate the carrier &lt;select&gt; with available carriers (value = slug, label = display name).
   */
  populateCarrierDropdown() {
    const carrierSelect = document.getElementById("carrier-select");
    if (!carrierSelect) return;

    const carriers = Object.keys(this.carrierGroups);
    const ph =
      (this.strings.placeholder_carrier || "Carrier") +
      (carriers.length ? " *" : "");

    if (carriers.length === 0) {
      carrierSelect.innerHTML = `<option value="">${this.escapeHtml(this.strings.no_carriers_available || "No carriers available")}</option>`;
      carrierSelect.value = "";
      document
        .getElementById("carrier-float-wrap")
        ?.classList.remove("has-value");
      this.toggleCarrierDependentFields();
      this.syncSelectFloatLabels();
      return;
    }

    let html = `<option value="">${this.escapeHtml(ph)}</option>`;
    carriers.forEach((carrier) => {
      const value = carrier.toLowerCase().replace(/\s+/g, "-");
      html += `<option value="${this.escapeHtml(value)}">${this.escapeHtml(carrier)}</option>`;
    });
    carrierSelect.innerHTML = html;
    carrierSelect.value = "";
    document
      .getElementById("carrier-float-wrap")
      ?.classList.remove("has-value");
    this.toggleCarrierDependentFields();
    this.syncSelectFloatLabels();
  }

  /**
   * Restore PO Number, Shipping Address, Shipping Instructions, and Packing List Notes from cart attributes.
   */
  restoreCheckoutFieldsFromCart() {
    const attrs = this.cart?.attributes || {};
    if (!attrs || typeof attrs !== "object") return;

    // PO Number
    const po = attrs._po_number || "";
    const poEl = document.getElementById("po-number");
    if (poEl && String(po).trim()) poEl.value = String(po).trim();

    // Shipping Instructions
    const instructions =
      attrs._checkout_shipping_instructions ??
      attrs._b2b_shipping_instructions ??
      "";
    const instructionsEl = document.getElementById("shipping-instructions");
    if (instructionsEl && String(instructions).trim())
      instructionsEl.value = String(instructions).trim();

    // Packing List Notes
    const packingNotes =
      attrs._checkout_packing_list_notes ?? attrs._b2b_packing_list_notes ?? "";
    const packingEl = document.getElementById("packing-list-notes");
    if (packingEl && String(packingNotes).trim())
      packingEl.value = String(packingNotes).trim();

    // Expand instructions section if we restored any value
    if (
      (instructions && instructions.trim()) ||
      (packingNotes && packingNotes.trim())
    ) {
      const section = document.getElementById("instructions-section");
      const toggle = document.getElementById("toggle-instructions");
      if (section) section.classList.add("visible");
      if (toggle) toggle.setAttribute("aria-expanded", "true");
    }

    // Shipping Address type and fields
    const addressType = (attrs._shipping_address_type || "current")
      .toString()
      .trim();
    const currentDetails = document.getElementById("current-address-details");
    const newAddressForm = document.getElementById("new-address-form");
    const addressbookSection = document.getElementById("addressbook-section");
    const addressCurrentRadio = document.getElementById("address-current");
    const addressNewRadio = document.getElementById("address-new");
    const addressBookRadio = document.getElementById("address-book");

    if (addressType === "current") {
      if (addressCurrentRadio) addressCurrentRadio.checked = true;
      if (currentDetails) currentDetails.style.display = "block";
      if (newAddressForm) newAddressForm.style.display = "none";
      if (addressbookSection) addressbookSection.style.display = "none";
      const attention = attrs._attention ?? "";
      const attentionEl = document.getElementById("attention-field");
      if (attentionEl && String(attention).trim())
        attentionEl.value = String(attention).trim();
    } else if (addressType === "new") {
      if (addressNewRadio) addressNewRadio.checked = true;
      if (currentDetails) currentDetails.style.display = "none";
      if (newAddressForm) newAddressForm.style.display = "block";
      if (addressbookSection) addressbookSection.style.display = "none";

      let custom = {};
      try {
        const raw = attrs._checkout_custom_shipping_address_json;
        if (raw && typeof raw === "string") custom = JSON.parse(raw) || {};
      } catch (_) {}
      const set = (id, val) => {
        const el = document.getElementById(id);
        if (el && val != null && String(val).trim() !== "")
          el.value = String(val).trim();
      };
      set("new-business-name", custom.company);
      set("new-attention", custom.attention);
      set("new-address-1", custom.address1);
      set("new-address-2", custom.address2);
      set("new-city", custom.city);
      set("new-state", custom.state);
      set("new-zip", custom.zip);
      set("new-phone", custom.phone);
      const countryEl = document.getElementById("new-country");
      if (countryEl && custom.country)
        countryEl.value = String(custom.country).trim();
      const saveCheckbox = document.getElementById("save-to-addressbook");
      if (saveCheckbox && custom.save_to_addressbook)
        saveCheckbox.checked = !!custom.save_to_addressbook;
    } else if (addressType === "addressbook") {
      if (addressBookRadio) addressBookRadio.checked = true;
      if (currentDetails) currentDetails.style.display = "none";
      if (newAddressForm) newAddressForm.style.display = "none";
      if (addressbookSection) addressbookSection.style.display = "block";

      const addressbookId =
        attrs._addressbook_id ??
        (() => {
          try {
            const raw = attrs._checkout_custom_shipping_address_json;
            if (raw && typeof raw === "string") {
              const parsed = JSON.parse(raw) || {};
              return parsed.addressbook_id || "";
            }
          } catch (_) {}
          return "";
        })();
      const attention = attrs._attention ?? "";
      const address = this.personalAddresses.find(
        (a) => a && String(a.id) === String(addressbookId),
      );
      if (address) {
        this.selectedAddressBookAddress = address;
        const abs = document.getElementById("addressbook-select");
        if (abs) abs.value = String(address.id);
        const attentionEl = document.getElementById("addressbook-attention");
        if (attentionEl && String(attention).trim())
          attentionEl.value = String(attention).trim();
        this.updateShippingAddressBookSelectedDetails();
      }
    }
    this.syncInputFloatLabels();
    this.syncSelectFloatLabels();
  }

  /**
   * Restore Shipping Method section from cart attributes (persisted from previous Continue).
   * Option 1 Best Way: carrier/condition from default metafield, freight/account empty.
   * Option 2 Select a Carrier: restore carrier, shipping condition, freight term, account number.
   */
  restoreShippingMethodFromCart() {
    const attrs = this.cart?.attributes || {};
    const type =
      attrs._shipping_method_type ||
      (attrs._checkout_shipping_method_carrier_code ? "select_carrier" : "");
    const carrierCode = (attrs._checkout_shipping_method_carrier_code || "")
      .toString()
      .trim();
    const carrierDesc = (
      attrs._checkout_shipping_method_carrier_description || ""
    )
      .toString()
      .trim();
    const conditionCode = (attrs._shipping_condition || "").toString().trim();
    const freightCode = (attrs._freight_term || "").toString().trim();
    const accountNumber = (
      attrs._checkout_shipping_method_account_number ||
      attrs._customer_carrier_account_number ||
      ""
    )
      .toString()
      .trim();

    if (type !== "select_carrier" || (!carrierCode && !carrierDesc)) {
      const bestWayRadio = document.querySelector(
        'input[name="shipping_method"][value="best_way"]',
      );
      if (bestWayRadio) bestWayRadio.checked = true;
      this.clearCarrierSelections();
      const carrierOptions = document.getElementById("carrier-options");
      if (carrierOptions) carrierOptions.style.display = "none";
      return;
    }

    const selectCarrierRadio = document.querySelector(
      'input[name="shipping_method"][value="select_carrier"]',
    );
    if (!selectCarrierRadio) return;
    selectCarrierRadio.checked = true;
    const carrierOptions = document.getElementById("carrier-options");
    if (carrierOptions) carrierOptions.style.display = "block";

    let carrierName = carrierDesc
      ? Object.keys(this.carrierGroups).find(
          (c) => c.toLowerCase().trim() === carrierDesc.toLowerCase().trim(),
        )
      : null;
    if (!carrierName && (carrierCode || carrierDesc)) {
      const firstRate = this.shippingRates.find(
        (r) =>
          (r.carrierCode && String(r.carrierCode).trim() === carrierCode) ||
          (r.carrierDescription &&
            String(r.carrierDescription).trim() === carrierDesc),
      );
      if (firstRate) carrierName = firstRate.carrierDescription || "";
    }
    if (!carrierName) return;

    const carrierSlug = carrierName.toLowerCase().replace(/\s+/g, "-");
    const carrierSelect = document.getElementById("carrier-select");
    if (carrierSelect) {
      carrierSelect.value = carrierSlug;
      const carrierWrap = document.getElementById("carrier-float-wrap");
      if (carrierWrap) carrierWrap.classList.add("has-value");
    }
    this.populateServiceDropdown(carrierName);

    const rates = this.carrierGroups[carrierName] || [];
    const selectedRate = conditionCode
      ? rates.find(
          (r) =>
            (r.handle && String(r.handle).trim() === conditionCode) ||
            (r.shippingConditionCode &&
              String(r.shippingConditionCode).trim() === conditionCode),
        )
      : rates[0];
    if (!selectedRate) {
      this.toggleCarrierDependentFields();
      return;
    }

    const serviceSelect = document.getElementById("service-select");
    if (serviceSelect) {
      serviceSelect.value = selectedRate.handle || "";
      const serviceWrap = document.getElementById("service-float-wrap");
      if (serviceWrap) serviceWrap.classList.add("has-value");
    }
    this.updateShippingSummary();
    this.populateFreightTerms(selectedRate);

    const freightInputSingle = document.querySelector(
      'input[name="freight_term"][data-single-freight-term="true"]',
    );
    const freightInputRadio = freightCode
      ? document.querySelector(
          `input[name="freight_term"][value="${this.escapeHtml(freightCode)}"]`,
        )
      : null;
    const freightInput =
      freightInputSingle ||
      freightInputRadio ||
      document.querySelector('input[name="freight_term"]');
    if (freightInput) {
      if (freightInput.hasAttribute("data-single-freight-term")) {
        freightInput.value = freightCode || freightInput.value;
      } else {
        freightInput.checked = true;
      }
    }

    const accountInput = document.getElementById("carrier-account");
    if (accountInput && accountNumber) accountInput.value = accountNumber;

    this.toggleCarrierDependentFields();
    this.syncSelectFloatLabels();
  }

  /**
   * Show or hide service dropdown and carrier-dependent field based on selections.
   * Service dropdown appears when a carrier is selected.
   * Carrier-dependent field (e.g. account number) appears when a carrier shipping option is selected.
   */
  toggleCarrierDependentFields() {
    const carrierHidden = document.getElementById("carrier-select");
    const serviceHidden = document.getElementById("service-select");
    const serviceSelectField = document.getElementById("service-select-field");
    const carrierAccountField = document.getElementById(
      "carrier-account-field",
    );
    const isCarrierSelected = carrierHidden?.value !== "";
    const isServiceSelected = serviceHidden?.value !== "";
    const invoiceOptionChecked = !!document.querySelector(
      'input[name="freight_term"]:checked[data-invoice-option="true"]',
    );

    if (serviceSelectField)
      serviceSelectField.style.display = isCarrierSelected ? "block" : "none";
    if (carrierAccountField) {
      const showAccount =
        isCarrierSelected && isServiceSelected && !invoiceOptionChecked;
      carrierAccountField.style.display = showAccount ? "block" : "none";
    }
  }

  /**
   * Clear all carrier and service selections and hide dependent fields (when "Select a carrier" is not chosen).
   */
  clearCarrierSelections() {
    const carrierSelect = document.getElementById("carrier-select");
    const serviceSelect = document.getElementById("service-select");
    const serviceSelectField = document.getElementById("service-select-field");
    const carrierAccountField = document.getElementById(
      "carrier-account-field",
    );
    const carrierAccountInput = document.getElementById("carrier-account");

    const carrierPh =
      (this.strings.placeholder_carrier || "Carrier") + " *";
    if (carrierSelect) {
      carrierSelect.innerHTML = `<option value="">${this.escapeHtml(carrierPh)}</option>`;
      carrierSelect.value = "";
      document
        .getElementById("carrier-float-wrap")
        ?.classList.remove("has-value");
    }

    const servicePh =
      this.strings.select_carrier_first || "Select a carrier first";
    if (serviceSelect) {
      serviceSelect.innerHTML = `<option value="">${this.escapeHtml(servicePh)}</option>`;
      serviceSelect.value = "";
      document
        .getElementById("service-float-wrap")
        ?.classList.remove("has-value");
    }
    if (serviceSelectField) serviceSelectField.style.display = "none";
    if (carrierAccountField) carrierAccountField.style.display = "none";
    if (carrierAccountInput) carrierAccountInput.value = "";
    const freightTermsField = document.getElementById("freight-terms-field");
    const freightTermsOptions = document.getElementById(
      "freight-terms-options",
    );
    if (freightTermsField) freightTermsField.style.display = "none";
    if (freightTermsOptions) freightTermsOptions.innerHTML = "";

    this.updateShippingSummary();
    this.syncInputFloatLabels();
    this.syncSelectFloatLabels();
  }

  /**
   * Populate the service &lt;select&gt; for a carrier (option value = rate handle, data-price on option).
   */
  populateServiceDropdown(carrierName) {
    const serviceSelect = document.getElementById("service-select");
    if (!serviceSelect) return;

    const rates = this.carrierGroups[carrierName] || [];
    const phBase =
      this.strings.placeholder_shipping_options || "Shipping Options";

    if (rates.length === 0) {
      serviceSelect.innerHTML = `<option value="">${this.escapeHtml("No services available")}</option>`;
      serviceSelect.value = "";
      document
        .getElementById("service-float-wrap")
        ?.classList.remove("has-value");
      this.populateFreightTerms(null);
      this.updateShippingSummary();
      this.syncSelectFloatLabels();
      return;
    }

    let html = `<option value="">${this.escapeHtml(phBase + " *")}</option>`;
    rates.forEach((rate) => {
      const serviceName = this.extractServiceName(rate, carrierName);
      const price = this.formatShippingPrice(rate);
      const displayText = price ? `${serviceName} - ${price}` : serviceName;
      const priceAmount = rate.priceV2?.amount || 0;
      const handle = rate.handle || "";
      html += `<option value="${this.escapeHtml(handle)}" data-price="${priceAmount}">${this.escapeHtml(displayText)}</option>`;
    });
    serviceSelect.innerHTML = html;
    serviceSelect.value = "";
    document
      .getElementById("service-float-wrap")
      ?.classList.remove("has-value");
    this.populateFreightTerms(null);
    this.updateShippingSummary();
    this.syncSelectFloatLabels();
  }

  /**
   * Extract service name from rate (removing carrier prefix)
   */
  extractServiceName(rate, carrierName) {
    let title = rate.title || rate.handle || "Standard";

    // Remove carrier name from title if present
    const carrierPattern = new RegExp(carrierName + "\\s*", "gi");
    title = title.replace(carrierPattern, "").trim();

    // Clean up common prefixes
    title = title.replace(/^[-:\s]+/, "").trim();

    return title || "Standard";
  }

  /**
   * Format shipping price for display
   */
  formatShippingPrice(rate) {
    const amount = parseFloat(rate.priceV2?.amount || 0);
    if (amount === 0) return "FREE";

    return new Intl.NumberFormat(this.getMoneyFormatLocale(), {
      style: "currency",
      currency: rate.priceV2?.currencyCode || this.getCheckoutCurrency(),
      currencyDisplay: "narrowSymbol",
    }).format(amount);
  }

  /**
   * True once every cart line resolves a realtime unit price (needed to compare against the
   * surcharge threshold).
   * @returns {boolean}
   */
  isRtpPricingReadyForCart() {
    const items = this.cart?.items || [];
    return items.length > 0 && items.every((item) => this.getRtpUnitCents(item) != null);
  }

  /**
   * Hide the surcharge row and zero its total hook.
   * @param {HTMLElement} shippingDisplay
   */
  setSurchargeRowHidden(shippingDisplay) {
    const row = shippingDisplay.closest(".b2b-checkout__summary-row");
    if (row) row.style.display = "none";
    shippingDisplay.classList.add("b2b-checkout__summary-value--free");
    if (shippingDisplay.dataset.surchargesCents !== "0") {
      shippingDisplay.dataset.surchargesCents = "0";
      window.RTP?.refreshEstimatedCartTotals?.();
    }
  }

  /**
   * Poll until RTP prices are available, then re-run the surcharge summary.
   */
  scheduleSurchargeSummaryRetry() {
    if (this._surchargeRetryTimer) return;
    const started = Date.now();
    this._surchargeRetryTimer = setInterval(() => {
      if (this.isRtpPricingReadyForCart()) {
        clearInterval(this._surchargeRetryTimer);
        this._surchargeRetryTimer = null;
        this.updateShippingSummary();
      } else if (Date.now() - started > 20000) {
        clearInterval(this._surchargeRetryTimer);
        this._surchargeRetryTimer = null;
      }
    }, 250);
  }

  /**
   * Update shipping cost in order summary (reads data-price from selected service option)
   */
  updateShippingSummary() {
    const serviceSelect = document.getElementById("service-select");
    const shippingDisplay = document.getElementById("summary-shipping");
    const totalDisplay = document.getElementById("summary-total");

    if (!shippingDisplay) return;

    // Surcharge only applies to orders under $500; must match the threshold applied server-side
    // in checkout-with-billing so the displayed amount matches what the draft order charges.
    const SURCHARGE_THRESHOLD = 500;
    const surchargesStored = this.cart?.attributes?._surcharges;
    if (surchargesStored != null && surchargesStored !== "" && !this.isRtpPricingReadyForCart()) {
      // Can't tell which side of the threshold we're on until RTP prices land; a $0 subtotal would
      // wrongly apply the surcharge. Keep the row hidden (and out of the total) and retry.
      this.setSurchargeRowHidden(shippingDisplay);
      this.scheduleSurchargeSummaryRetry();
      return;
    }
    const subtotal = (this.cart?.items || []).reduce((sum, item) => {
      const unitCents = this.getRtpUnitCents(item) ?? item.price ?? 0;
      return sum + (unitCents * (item.quantity || 0));
    }, 0) / 100;
    const surchargesRaw = this.cart?.attributes?._surcharges;
    let price;
    if (surchargesRaw != null && surchargesRaw !== "" && subtotal < SURCHARGE_THRESHOLD) {
      const surchargeValue = parseFloat(surchargesRaw);
      price = (Number.isFinite(surchargeValue) && surchargeValue >= 0) ? surchargeValue : 0;
    } else if (surchargesRaw != null && surchargesRaw !== "") {
      price = 0;
    } else {
      if (!serviceSelect) return;
      const opt = serviceSelect.selectedOptions?.[0];
      price = parseFloat(opt?.getAttribute?.("data-price") || 0);
    }

    const checkoutCurrency = this.getCheckoutCurrency();
    const moneyLocale = this.getMoneyFormatLocale();

    if (price === 0) {
      shippingDisplay.classList.add("b2b-checkout__summary-value--free");
      // Hide the shipping row when surcharge is $0
      const shippingRow = shippingDisplay.closest(".b2b-checkout__summary-row");
      if (shippingRow) shippingRow.style.display = "none";
    } else {
      shippingDisplay.classList.remove("b2b-checkout__summary-value--free");
      const shippingRow = shippingDisplay.closest(".b2b-checkout__summary-row");
      if (shippingRow) shippingRow.style.display = "";
      // Update the visible text with the actual surcharge amount
      shippingDisplay.textContent = new Intl.NumberFormat(moneyLocale, {
        style: "currency",
        currency: checkoutCurrency,
        currencyDisplay: "narrowSymbol",
      }).format(price);
    }
    const prevCents = shippingDisplay.dataset.surchargesCents;
    shippingDisplay.dataset.surchargesCents = String(Math.round(price * 100));
    // RTP folds this DOM value into the estimated total, so re-render it whenever it changes.
    if (prevCents !== shippingDisplay.dataset.surchargesCents) {
      window.RTP?.refreshEstimatedCartTotals?.();
    }

    // Update total (subtotal + shipping/surcharges).
    // RTP owns any element with data-cart-total-price (see renderOrderSummary) — cart.total_price
    // reflects Shopify's stored line prices, not the realtime-pricing override, so writing it here
    // would stomp on (or race) whatever RTP has already rendered, showing $0 for RTP-priced items.
    if (totalDisplay && this.cart && !totalDisplay.hasAttribute("data-cart-total-price")) {
      const subtotalDollars = this.cart.total_price / 100;
      const total = subtotalDollars;
      const formattedTotal = new Intl.NumberFormat(moneyLocale, {
        style: "currency",
        currency: checkoutCurrency,
        currencyDisplay: "narrowSymbol",
      }).format(total);
      totalDisplay.textContent = formattedTotal;
      document.querySelectorAll(".js-summary-total").forEach((el) => {
        if (el !== totalDisplay && !el.hasAttribute("data-cart-total-price")) el.textContent = formattedTotal;
      });
    }
  }

  /**
   * Get attention string for an address. Metafield keys may be raw GID or GID + "?model_name=CustomerAddress".
   * @param {{ id?: string }} address - address object with id (e.g. gid://shopify/MailingAddress/123)
   * @returns {string}
   */
  getAttentionForAddress(address) {
    if (!address?.id || !this.customerAttentions) return "";
    const id = String(address.id);
    return (
      this.customerAttentions[id] ||
      this.customerAttentions[id + "?model_name=CustomerAddress"] ||
      ""
    );
  }

  /**
   * Normalize address from /api/customer/addresses to checkout shape (id, label, address1, provinceCode, countryCode, etc.)
   */
  normalizeAddressForCheckout(address, index) {
    return {
      id: address.id,
      label: address.company || address.label || `Address ${index + 1}`,
      address1: address.address1 || "",
      address2: address.address2 || "",
      city: address.city || "",
      provinceCode:
        address.province || address.state_code || address.provinceCode || "",
      zip: address.zip || "",
      countryCode:
        address.country_code ||
        address.countryCodeV2 ||
        address.countryCode ||
        address.country ||
        "",
      firstName: address.firstName || "",
      lastName: address.lastName || "",
      billing_address_external_id: address.billing_address_external_id || "",
    };
  }

  /**
   * Fetch personal addresses from /api/customer/addresses (same as rs-address-book-list)
   */
  async fetchPersonalAddresses() {
    const customerId = window.__st?.cid || null;
    if (!customerId) {
      return;
    }

    try {
      const response = await fetch(
        `${GADGET_API_BASE}/api/customer/addresses?customer_id=gid://shopify/Customer/${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${window.myJWT || ""}`,
          },
        },
      );

      const data = await response.json();
      const rawAddresses = data.addresses || data || [];
      if (data.attentions && typeof data.attentions === "object") {
        this.customerAttentions = {
          ...this.customerAttentions,
          ...data.attentions,
        };
      }

      if (response.ok && Array.isArray(rawAddresses)) {
        this.personalAddresses = rawAddresses.map((addr, i) =>
          this.normalizeAddressForCheckout(addr, i),
        );
        this.renderAddressBookList();
      }
    } catch (error) {
      // Fetch personal addresses failed
    }
  }

  /**
   * Render the personal address book as a native &lt;select&gt;
   */
  renderAddressBookList() {
    const loadingEl = document.getElementById("addressbook-loading");
    const selectEl = document.getElementById("addressbook-select");
    const shippingAddressbookOption = document
      .getElementById("address-book")
      ?.closest(".b2b-checkout__radio-option");

    if (loadingEl) loadingEl.style.display = "none";

    if (!this.personalAddresses.length) {
      if (shippingAddressbookOption)
        shippingAddressbookOption.style.display = "none";
      const addressbookRadio = document.getElementById("address-book");
      if (addressbookRadio?.checked) {
        const currentRadio = document.getElementById("address-current");
        if (currentRadio) {
          currentRadio.checked = true;
          this.initializeVisibleStates();
        }
      }
      if (selectEl) {
        const ph = this.strings.placeholder_select || "Select...";
        selectEl.innerHTML = `<option value="">${this.escapeHtml(ph)}</option>`;
      }
      return;
    }

    if (shippingAddressbookOption) shippingAddressbookOption.style.display = "";
    if (selectEl) {
      const ph = this.strings.placeholder_select || "Select...";
      let html = `<option value="">${this.escapeHtml(ph)}</option>`;
      this.personalAddresses.forEach((address, index) => {
        const label = address.label || `Address ${index + 1}`;
        html += `<option value="${this.escapeHtml(String(address.id))}">
          ${this.escapeHtml(label)} - 
          ${this.escapeHtml(address.address1)}
          ${this.escapeHtml(address.address2)}
          ${this.escapeHtml(address.city)}
          ${this.escapeHtml(address.provinceCode)},
          ${this.escapeHtml(address.zip)}
        </option>`;
      });
      selectEl.innerHTML = html;
      if (this.selectedAddressBookAddress) {
        selectEl.value = String(this.selectedAddressBookAddress.id);
      }
    }
  }

  /**
   * Delegated change handler for address book &lt;select&gt; (bound once on #b2b-checkout).
   */
  bindAddressBookNativeSelect() {
    const root = document.getElementById("b2b-checkout");
    if (!root || root.hasAttribute("data-addressbook-select-bound")) return;
    root.setAttribute("data-addressbook-select-bound", "true");
    root.addEventListener("change", (e) => {
      if (e.target?.id !== "addressbook-select") return;
      const sel = e.target;
      const id = sel.value?.trim();
      if (!id) {
        this.selectedAddressBookAddress = null;
        const attentionEl = document.getElementById("addressbook-attention");
        if (attentionEl) attentionEl.value = "";
      } else {
        this.selectedAddressBookAddress = this.personalAddresses.find(
          (a) => String(a.id) === id,
        );
        if (this.selectedAddressBookAddress) {
          const attentionEl = document.getElementById("addressbook-attention");
          if (attentionEl)
            attentionEl.value = this.getAttentionForAddress(
              this.selectedAddressBookAddress,
            );
        }
      }
      this.clearFieldError("addressbook-select");
      this.updateShippingAddressBookSelectedDetails();
    });
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text || "";
    return div.innerHTML;
  }

  /**
   * Format address parts for display (company + single line)
   */
  formatAddressForDisplay(address) {
    if (!address) return { company: "", line: "" };
    const company = address.label || "";
    const line = [
      address.address1,
      address.address2,
      address.city,
      address.provinceCode,
      address.zip,
      address.countryCode,
    ]
      .filter(Boolean)
      .join(", ");
    return { company, line };
  }

  /**
   * Update shipping "Select from address book" selected-details block (company, address line, attention visible)
   */
  updateShippingAddressBookSelectedDetails() {
    const detailsEl = document.getElementById("addressbook-selected-details");
    const companyEl = document.getElementById("addressbook-selected-company");
    const addressEl = document.getElementById("addressbook-selected-address");
    if (!detailsEl || !companyEl || !addressEl) return;
    if (this.selectedAddressBookAddress) {
      const { company, line } = this.formatAddressForDisplay(
        this.selectedAddressBookAddress,
      );
      companyEl.textContent = company;
      addressEl.textContent = line;
      detailsEl.style.display = "";
    } else {
      companyEl.textContent = "";
      addressEl.textContent = "";
      detailsEl.style.display = "none";
    }
    this.syncInputFloatLabels();
    this.syncSelectFloatLabels();
  }

  /**
   * Service &lt;select&gt;: shipping price, freight terms, validation (once, delegated).
   */
  bindServiceSelectChange() {
    const root = document.getElementById("b2b-checkout");
    if (!root || root.hasAttribute("data-service-select-bound")) return;
    root.setAttribute("data-service-select-bound", "true");
    root.addEventListener("change", (e) => {
      if (e.target?.id !== "service-select") return;
      const serviceSelect = e.target;
      const wrap = document.getElementById("service-float-wrap");
      if (serviceSelect.value) wrap?.classList.add("has-value");
      else wrap?.classList.remove("has-value");
      this.updateShippingSummary();
      this.toggleCarrierDependentFields();
      const selectedRate = this.getSelectedRate();
      this.populateFreightTerms(selectedRate);
      const serviceField = serviceSelect.closest(".b2b-checkout__field");
      if (serviceField) {
        serviceField.classList.remove("b2b-checkout__field--invalid");
        serviceField.querySelector(".b2b-checkout__field-error")?.remove();
      }
      serviceSelect.removeAttribute("aria-invalid");
      serviceSelect.removeAttribute("aria-describedby");
    });
  }

  /**
   * Bind events for address book items (legacy radio list; kept if needed elsewhere)
   */
  bindAddressBookItemEvents() {
    // Select address
    const radios = document.querySelectorAll(
      'input[name="addressbook_selection"]',
    );
    radios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const addressId = e.target.value;
        this.selectedAddressBookAddress = this.personalAddresses.find(
          (a) => a.id === addressId,
        );
        const attentionEl = document.getElementById("addressbook-attention");
        if (attentionEl && this.selectedAddressBookAddress) {
          attentionEl.value = this.getAttentionForAddress(
            this.selectedAddressBookAddress,
          );
        }

        // Update visual selection
        document
          .querySelectorAll(".b2b-checkout__addressbook-item")
          .forEach((item) => {
            item.classList.remove("selected");
          });
        e.target
          .closest(".b2b-checkout__addressbook-item")
          .classList.add("selected");
        this.updateShippingAddressBookSelectedDetails();
      });
    });

    // Delete address
    const deleteButtons = document.querySelectorAll(
      ".b2b-checkout__addressbook-item-delete",
    );
    deleteButtons.forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const addressId = btn.dataset.addressId;
        if (confirm("Remove this address from your address book?")) {
          await this.deletePersonalAddress(addressId);
        }
      });
    });
  }

  /**
   * Delete a personal address via /api/customer/address-delete (same as rs-address-book-list)
   */
  async deletePersonalAddress(addressId) {
    const customerId = window.__st?.cid || null;
    if (!customerId) return;

    const addressGid =
      addressId && !String(addressId).startsWith("gid://")
        ? `gid://shopify/CustomerAddress/${addressId}`
        : addressId;

    try {
      const response = await fetch(
        `${GADGET_API_BASE}/api/customer/address-delete?customerId=gid://shopify/Customer/${customerId}&addressId=${encodeURIComponent(addressGid)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${window.myJWT || ""}`,
          },
        },
      );

      if (response.ok) {
        await this.fetchPersonalAddresses();
        if (this.selectedAddressBookAddress?.id === addressId) {
          this.selectedAddressBookAddress = null;
        }
        this.showMessage(
          this.strings.address_removed || "Address removed from address book",
          "success",
        );
      } else {
        this.showMessage(
          this.strings.address_remove_failed || "Failed to remove address",
          "error",
        );
      }
    } catch (error) {
      this.showMessage(
        this.strings.address_remove_failed || "Failed to remove address",
        "error",
      );
    }
  }

  /**
   * Save a new address via POST /api/customer/address.
   * Sends attention; backend should persist it to customer metafield (customer.attentions, JSON)
   * keyed by the created address GID so it loads when selecting this address later.
   */
  async saveToPersonalAddressBook(addressData) {
    const customerId = window.__st?.cid || null;
    if (!customerId) {
      return false;
    }

    try {
      const attention =
        addressData.attention != null &&
        String(addressData.attention).trim() !== ""
          ? String(addressData.attention).trim()
          : [addressData.firstName, addressData.lastName]
              .filter(Boolean)
              .join(" ")
              .trim() || null;
      const response = await fetch(`${GADGET_API_BASE}/api/customer/address`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.myJWT || ""}`,
        },
        body: JSON.stringify({
          customer_id: `gid://shopify/Customer/${customerId}`,
          company: addressData.label || "",
          attention,
          address1: addressData.address1,
          address2: addressData.address2 || null,
          city: addressData.city,
          state_code: addressData.state || addressData.provinceCode || "",
          zip: addressData.zip,
          phone: addressData.phone || null,
          country_code: addressData.country || addressData.countryCode || "",
        }),
      });

      if (response.ok) {
        await this.fetchPersonalAddresses();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * True when any cart line has a zero unit price (realtime pricing not applied).
   */
  cartHasZeroPriceItems(cart = this.cart) {
    if (typeof window.cartHasZeroPriceItems === "function") {
      return window.cartHasZeroPriceItems(cart);
    }
    return (Array.isArray(cart?.items) ? cart.items : []).some(
      (item) => (item.final_price ?? item.price ?? 0) === 0,
    );
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js");
      this.cart = await response.json();
      if (this.cartHasZeroPriceItems()) {
        window.location.href = Theme?.routes?.cart_url || "/cart";
        return;
      }
    } catch (error) {
      this.cart = { items: [], total_price: 0, item_count: 0 };
    }
  }

  /**
   * Render cart data in Order Summary
   */
  render() {
    this.renderOrderSummary();
  }

  /**
   * Render order summary sidebar
   */
  renderOrderSummary() {
    const subtotalEl = document.getElementById("summary-subtotal");
    const skuCountEl = document.getElementById("summary-sku-count");
    const orderItemsEl = document.getElementById("b2b-checkout-order-items");

    if (!this.cart || !this.cart.items.length) {
      const zero = this.formatMoney(0);
      if (subtotalEl && !subtotalEl.hasAttribute("data-cart-total-price")) subtotalEl.textContent = zero;
      document.querySelectorAll(".js-summary-total").forEach((el) => {
        if (!el.hasAttribute("data-cart-total-price")) el.textContent = zero;
      });
      if (skuCountEl) skuCountEl.textContent = "0";
      if (orderItemsEl) orderItemsEl.innerHTML = "";
      return;
    }

    if (orderItemsEl) {
      orderItemsEl.innerHTML = this.cart.items
        .map((item) => this.renderOrderItem(item))
        .join("");
    }

    // RTP owns any element with data-cart-total-price — skip writing USD cart prices to those.
    if (subtotalEl && !subtotalEl.hasAttribute("data-cart-total-price")) {
      subtotalEl.textContent = this.formatMoney(this.cart.total_price);
    }
    document.querySelectorAll(".js-summary-total").forEach((el) => {
      if (!el.hasAttribute("data-cart-total-price")) {
        el.textContent = this.formatMoney(this.cart.total_price);
      }
    });
    if (skuCountEl) {
      const uniqueSkuCount = new Set(
        this.cart.items
          .map((item) =>
            (item.sku || this.extractSku(item) || "").toString().trim(),
          )
          .filter(Boolean),
      ).size;
      skuCountEl.textContent = uniqueSkuCount;
    }
  }

  /**
   * Render single order item
   */
  getPrimaryImageUrlForItem(item) {
    const list = window.B2B_CHECKOUT_ITEMS_SALES_DATA;
    if (!Array.isArray(list)) return null;
    const entry = list.find(
      (e) => e && Number(e.variant_id) === Number(item.variant_id),
    );
    return entry?.primary_image_url && entry.primary_image_url !== ""
      ? entry.primary_image_url
      : null;
  }

  renderOrderItem(item) {
    const sku = item.sku || this.extractSku(item);
    const rtpUnitCents = this.getRtpUnitCents(item);
    const lineCents = rtpUnitCents != null ? rtpUnitCents * (item.quantity || 0) : item.final_line_price;
    const price = this.formatMoney(lineCents);
    const imageUrl =
      this.getPrimaryImageUrlForItem(item) ||
      item.featured_image?.url ||
      item.image?.url;

    return `
      <div class="b2b-checkout__order-item">
        <div class="b2b-checkout__order-item-image-wrap">
          <div class="b2b-checkout__order-item-image">
            ${
              imageUrl
                ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(item.product_title)}" width="64" height="64" loading="lazy" />`
                : '<div class="b2b-checkout__order-item-no-image">No Image</div>'
            }
          </div>
          <span class="b2b-checkout__order-item-qty-badge" aria-label="${window.__t('quantity')}">${item.quantity}</span>
        </div>
        <div class="b2b-checkout__order-item-details">
          <div class="b2b-checkout__order-item-sku">${this.escapeHtml(String(sku))}</div>
          ${this.renderOrderItemAttributes(item)}
        </div>
        <div class="b2b-checkout__order-item-price">${price}</div>
      </div>
    `;
  }

  /**
   * Get sales area external_id from context cookie (same as cart/rs-product-card-sales-data).
   */
  getSalesAreaExternalId() {
    try {
      const cookies = (document.cookie || "").split(";").reduce((acc, s) => {
        const i = s.indexOf("=");
        if (i === -1) return acc;
        acc[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(
          s.slice(i + 1).trim(),
        );
        return acc;
      }, {});
      const ctx = cookies.context
        ? JSON.parse(decodeURIComponent(cookies.context))
        : null;
      if (!ctx) return "";
      const cand = ctx.sales_area;
      const saRaw = Array.isArray(cand)
        ? cand[0]?.fields || cand[0]
        : cand?.fields || cand || {};
      return saRaw?.external_id ?? "";
    } catch {
      return "";
    }
  }

  /**
   * Resolve company sales data for current sales area (same logic as rs-product-card-sales-data.js).
   */
  resolveCompanySalesData(salesData, externalId) {
    if (!salesData || !externalId) return null;
    let companySalesData = null;
    if (Array.isArray(salesData)) {
      companySalesData = salesData.find(
        (s) => s && s.sales_area === externalId,
      );
    } else if (typeof salesData === "object") {
      if (
        salesData.sales_area === externalId ||
        Object.keys(salesData).length === 0
      ) {
        companySalesData = salesData;
      } else if (salesData.value) {
        const value = Array.isArray(salesData.value)
          ? salesData.value.find((s) => s && s.sales_area === externalId) ||
            salesData.value[0] ||
            {}
          : salesData.value;
        companySalesData = value;
      }
    }
    return companySalesData;
  }

  /**
   * Get resolved sales data for a cart line item (Service Class, Min Qty, Multiple).
   */
  getSalesDataForItem(item) {
    const list = window.B2B_CHECKOUT_ITEMS_SALES_DATA;
    if (!Array.isArray(list)) return null;
    const entry = list.find(
      (e) => e && Number(e.variant_id) === Number(item.variant_id),
    );
    if (!entry || !entry.sales_data) return null;
    const externalId = this.getSalesAreaExternalId();
    return this.resolveCompanySalesData(entry.sales_data, externalId);
  }

  renderOrderItemAttributes(item) {
    const parts = [];
    const sd = this.getSalesDataForItem(item);
    if (sd) {
      if (sd.serv_lvl_days != null && sd.serv_lvl_days !== "") {
        parts.push(
          `${window.__t('service_class', { days: this.escapeHtml(String(sd.serv_lvl_days)) })} <span class="rs-mob-hidden">${window.__t('business_days')}</span><span class="rs-desktop-hidden">${window.__t('biz_days')}</span>`,
        );
      }
      const hasMin =
        sd.minimum_order_quantity != null && sd.minimum_order_quantity !== "";
      const hasMult =
        sd.ship_increment_quantity != null && sd.ship_increment_quantity !== "";
      if (hasMin || hasMult) {
        const minStr = hasMin
          ? this.escapeHtml(String(sd.minimum_order_quantity == 0 ? 1 : sd.minimum_order_quantity))
          : "—";
        const multStr = hasMult
          ? this.escapeHtml(String(sd.ship_increment_quantity))
          : "—";
        parts.push(`${window.__t('min_qty', { count: minStr })} | ${window.__t('multiple', { count: multStr })}`);
      }
    }
    if (parts.length === 0) {
      if (item.selling_plan_allocation?.selling_plan?.name) {
        parts.push(
          this.escapeHtml(item.selling_plan_allocation.selling_plan.name),
        );
      }
      const props = item.properties || {};
      const minQty = props._min_qty ?? props.min_qty;
      const multiple = props._multiple ?? props.multiple;
      if (minQty != null || multiple != null) {
        parts.push(
          `${window.__t('min_qty', { count: this.escapeHtml(String(minQty ?? "—")) })} | ${window.__t('multiple', { count: this.escapeHtml(String(multiple ?? "—")) })}`,
        );
      }
    }
    if (parts.length === 0) return "";
    return `<div class="b2b-checkout__order-item-attributes">${parts.join("<br />")}</div>`;
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Realtime-priced unit cents for a cart item, mirroring b2b-review-order.js's getRtpUnitCents.
   * item.price/item.line_price reflect Shopify's stored price, not the RTP override, and can be
   * $0 whenever the Cart Transform doesn't apply it (e.g. draft order checkout, which skips the
   * transform entirely and carries pricing explicitly instead).
   */
  getRtpUnitCents(item) {
    const override = item?.properties?._price_override;
    if (override != null && override !== '') {
      const parsed = Number(override);
      if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed * 100);
    }
    const sku = (item?.sku || this.extractSku(item) || '').trim();
    const list = window.RTP?.lastCartMaterialList;
    const aliases = window.RTP?.pricingSkuAliases;
    if (!Array.isArray(list) || !sku) return null;
    const match = list.find((m) => {
      const matNum = String(m.materialNumber ?? '').trim();
      if (matNum === sku) return true;
      const raws = aliases?.get(matNum);
      return raws ? raws.some((r) => String(r).trim() === sku) : false;
    });
    if (!match || match.netPrice == null) return null;
    return Math.round(Number(match.netPrice) * 100);
  }

  /**
   * Extract SKU from item data
   */
  extractSku(item) {
    if (item.handle) {
      return item.handle.toUpperCase().replace(/-/g, "-");
    }
    if (item.variant_title && item.variant_title !== "Default Title") {
      return item.variant_title;
    }
    if (item.variant_id) {
      return `VAR-${item.variant_id}`;
    }
    return "N/A";
  }

  /**
   * Format money using the active storefront currency.
   */
  formatMoney(cents) {
    return new Intl.NumberFormat(this.getMoneyFormatLocale(), {
      style: "currency",
      currency: this.getCheckoutCurrency(),
      currencyDisplay: "narrowSymbol",
    }).format(cents / 100);
  }

  /**
   * Bind all event handlers
   */
  bindEvents() {
    // Refresh cart when promo code is applied
    document.addEventListener("discount:update", this.handleDiscountUpdate.bind(this));

    // Shipping address radio toggle
    this.bindShippingAddressToggle();
    // Clear field errors as soon as the user edits (type/select) the control
    this.bindFieldErrorClearOnEdit();
    // Validate zip and phone on blur so invalid state shows when leaving the field
    this.bindBlurValidation();

    // Shipping method radio toggle
    this.bindShippingMethodToggle();

    // Shipping instructions toggle
    this.bindShippingInstructionsToggle();

    // Review order button
    this.bindReviewOrderButton();

    // Mobile order accordion
    this.bindOrderAccordion();

    // Initialize visible states
    this.initializeVisibleStates();

    // Country/state sync: state dropdown options depend on selected country
    this.bindCountryStateSync();

    // Real-time character filtering on address form inputs
    this.initInputValidation();

    // Re-validate zip when state changes
    this.bindStateChangeZipRevalidation();

    this.bindAddressBookNativeSelect();
    this.bindServiceSelectChange();
  }

  /**
   * Populate the state/province dropdown from B2B_CHECKOUT_STATES for the given country (US or CA).
   */
  populateStateOptionsForCountry(countryCode) {
    const stateSelect = document.getElementById("new-state");
    if (!stateSelect) return;
    const raw = window.B2B_CHECKOUT_STATES;
    const code = (countryCode).trim().toUpperCase();
    const states =
      raw && raw[code] !== undefined ? raw[code] : [];
    const placeholder = this.strings.placeholder_select || "Select...";
    stateSelect.innerHTML = "";
    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = placeholder;
    stateSelect.appendChild(placeholderOption);
    states.forEach(({ value, label }) => {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = label;
      stateSelect.appendChild(opt);
    });
    this.syncSelectFloatLabels();
  }

  /**
   * Bind country select change to update state dropdown options (US states vs CA provinces).
   */
  /**
   * Re-validate the ZIP code field when the state selection changes.
   */
  bindStateChangeZipRevalidation() {
    const stateSelect = document.getElementById("new-state");
    const zipInput = document.getElementById("new-zip");
    if (!stateSelect || !zipInput) return;

    stateSelect.addEventListener("change", () => {
      const zip = zipInput.value?.trim();
      if (!zip) return;

      const country = document.getElementById("new-country")?.value?.trim() || "";
      const state = stateSelect.value;

      // Clear any existing zip errors first
      this.clearFieldError("new-zip");
      this.clearInputValidationMessage(zipInput);

      if (!this.isValidZip(zip, country)) {
        this.setShippingAddressFieldError("new-zip", "Enter a valid ZIP code.");
      } else if (!this.isZipMatchingState(zip, state, country)) {
        this.setShippingAddressFieldError("new-zip", "ZIP code does not match the selected state.");
      }
    });
  }

  bindCountryStateSync() {
    const countrySelect = document.getElementById("new-country");
    if (!countrySelect) return;
    const country = countrySelect.value?.trim() || "";
    this.populateStateOptionsForCountry(country);
    countrySelect.addEventListener("change", () => {
      const newCountry = countrySelect.value?.trim() || "";
      this.populateStateOptionsForCountry(newCountry);
      const stateSelect = document.getElementById("new-state");
      if (stateSelect) stateSelect.value = "";
      this.syncSelectFloatLabels();
    });
  }

  bindOrderAccordion() {
    const toggle = document.getElementById(
      "b2b-checkout-order-accordion-toggle",
    );
    const accordion = document.querySelector("[data-order-accordion]");
    if (!toggle || !accordion) return;
    toggle.addEventListener("click", () => {
      const isCollapsed = accordion.classList.toggle("is-collapsed");
      toggle.setAttribute("aria-expanded", String(!isCollapsed));
    });
  }

  /**
   * When a promo code is applied, refresh cart and re-render order summary.
   */
  async handleDiscountUpdate() {
    await this.fetchCart();
    this.render();
  }

  /**
   * When the user types or changes a control, clear that field's validation error (border + message + aria).
   * Uses delegation on #b2b-checkout so PO, new address, carrier comboboxes, etc. are covered.
   */
  bindFieldErrorClearOnEdit() {
    const root = document.getElementById("b2b-checkout");
    if (!root) return;
    const tryClear = (el) => {
      if (!el || typeof el.matches !== "function") return;
      if (!el.matches("input, textarea, select")) return;
      if (
        el.matches(
          'input[type="radio"], input[type="checkbox"], input[type="hidden"]',
        )
      ) {
        return;
      }
      const field = el.closest(".b2b-checkout__field");
      if (!field || !field.classList.contains("b2b-checkout__field--invalid")) {
        return;
      }
      this.clearFieldErrorForElement(el);
    };
    root.addEventListener("input", (e) => tryClear(e.target));
    root.addEventListener("change", (e) => {
      if (e.target?.matches?.("select")) tryClear(e.target);
    });
  }

  /**
   * Validate zip and phone on blur so invalid state shows when leaving the field
   */
  bindBlurValidation() {
    const isNewAddressSelected = () =>
      document.querySelector('input[name="shipping_address"]:checked')
        ?.value === "new";

    // PO Number — always visible
    const poInput = document.getElementById("po-number");
    if (poInput) {
      poInput.addEventListener("input", () => {
        const { selectionStart, selectionEnd } = poInput;
        poInput.value = poInput.value.toUpperCase();
        poInput.setSelectionRange(selectionStart, selectionEnd);
      });
      poInput.addEventListener("blur", () => {
        const val = poInput.value?.trim();
        if (!val) {
          this.setFieldError("po-number", "Please enter a PO number.");
          return;
        }
        this.clearPoNumberFieldError();
      });
    }

    // Required new-address fields
    const requiredFields = [
      { id: "new-business-name", msg: "Enter a company name." },
      { id: "new-address-1", msg: "Enter a street address." },
      { id: "new-city", msg: "Enter a city." },
    ];

    requiredFields.forEach(({ id, msg }) => {
      const input = document.getElementById(id);
      if (!input) return;
      input.addEventListener("blur", () => {
        if (!isNewAddressSelected()) return;
        if (!input.value?.trim()) {
          this.setFieldError(id, msg);
        }
      });
    });

    // State select
    const stateSelect = document.getElementById("new-state");
    if (stateSelect) {
      stateSelect.addEventListener("blur", () => {
        if (!isNewAddressSelected()) return;
        const country =
          document.getElementById("new-country")?.value?.trim() || "";
        if (
          this.checkoutCountryHasStateOptions(country) &&
          !stateSelect.value?.trim()
        ) {
          this.setFieldError("new-state", "Select a state.");
        }
      });
    }

    // ZIP — with format and state-match validation
    const zipInput = document.getElementById("new-zip");
    if (zipInput) {
      zipInput.addEventListener("blur", () => {
        if (!isNewAddressSelected()) return;
        const zip = zipInput.value?.trim();
        const country = document.getElementById("new-country")?.value?.trim();
        if (!zip) {
          this.setFieldError("new-zip", "Enter a ZIP code.");
        } else if (!this.isValidZip(zip, country)) {
          this.setFieldError("new-zip", "Enter a valid ZIP code.");
        } else if (!this.isZipMatchingState(zip, document.getElementById("new-state")?.value, country)) {
          this.setFieldError("new-zip", "ZIP code does not match the selected state.");
        } else {
          this.clearFieldError("new-zip");
        }
      });
    }

    // Phone — with format validation
    const phoneInput = document.getElementById("new-phone");
    if (phoneInput) {
      phoneInput.addEventListener("blur", () => {
        if (!isNewAddressSelected()) return;
        const phone = phoneInput.value?.trim();
        if (!phone) {
          this.setFieldError("new-phone", "Enter a phone number.");
        } else if (!this.isValidPhone(phone)) {
          this.setFieldError("new-phone", "Enter a valid phone number.");
        } else {
          this.clearFieldError("new-phone");
        }
      });
    }
  }

  /**
   * Initialize real-time input validation/filtering on checkout address fields.
   * Strips disallowed characters on input based on the data-validate attribute.
   */
  initInputValidation() {
    const rules = {
      "new-business-name": { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "new-attention":     { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "new-address-1":     { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "new-address-2":     { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "attention-field":   { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "addressbook-attention": { regex: /[^a-zA-Z0-9 ]/g, msg: "Only letters, numbers, and spaces are allowed." },
      "new-city":          { regex: /[^a-zA-Z ]/g,    msg: "Only letters and spaces are allowed." },
      "new-phone":         { regex: /[^0-9+]/g,       msg: "Only numbers and + are allowed." },
    };
    const zipRules = {
      US: { regex: /[^0-9\-]/g,      msg: "Only numbers and dashes are allowed." },
      CA: { regex: /[^a-zA-Z0-9 \-]/g, msg: "Only letters, numbers, spaces, and dashes are allowed." },
    };

    // Use event delegation on the document to catch input events regardless of when elements render
    document.addEventListener("input", (e) => {
      const input = e.target;
      if (input.id === "new-zip") {
        const country = (document.getElementById("new-country")?.value || "").trim().toUpperCase();
        const rule = zipRules[country] || zipRules.US;
        const cursorPos = input.selectionStart;
        const original = input.value;
        const filtered = original.replace(rule.regex, "");
        if (filtered !== original) {
          input.value = filtered;
          const diff = original.length - filtered.length;
          input.setSelectionRange(cursorPos - diff, cursorPos - diff);
          this.showInputValidationMessage(input, rule.msg);
        }
        return;
      }

      const rule = rules[input.id];
      if (!rule) return;

      const cursorPos = input.selectionStart;
      const original = input.value;
      const filtered = original.replace(rule.regex, "");
      if (filtered !== original) {
        input.value = filtered;
        const diff = original.length - filtered.length;
        input.setSelectionRange(cursorPos - diff, cursorPos - diff);
        this.showInputValidationMessage(input, rule.msg);
      }
    });
  }

  /**
   * Show a temporary validation message below a checkout input field.
   * Auto-hides after 3 seconds.
   */
  /**
   * Show a validation message below a checkout input field.
   * @param {HTMLElement} input
   * @param {string} message
   * @param {boolean} persistent - if true, message stays until clearInputValidationMessage is called
   */
  showInputValidationMessage(input, message, persistent) {
    const field = input.closest(".b2b-checkout__field");
    if (!field) return;

    // Hide any existing field error to avoid stacking
    const existingError = field.querySelector(".b2b-checkout__field-error:not(.b2b-checkout__validation-message)");
    if (existingError) {
      existingError.style.display = "none";
    }

    const row = input.closest(".b2b-checkout__field-row");
    const msgId = input.id ? `${input.id}-validation` : "";

    let msgEl;
    if (row && row.parentNode && msgId) {
      // Place after the row as a full-width sibling
      msgEl = row.parentNode.querySelector(`#${CSS.escape(msgId)}`);
      if (!msgEl) {
        msgEl = document.createElement("span");
        msgEl.className = "b2b-checkout__validation-message b2b-checkout__field-error b2b-checkout__field-error--row-level";
        msgEl.id = msgId;
        msgEl.setAttribute("role", "alert");
        msgEl.setAttribute("data-for", input.id);
        row.insertAdjacentElement("afterend", msgEl);
      }
    } else {
      msgEl = field.querySelector(".b2b-checkout__validation-message");
      if (!msgEl) {
        msgEl = document.createElement("span");
        msgEl.className = "b2b-checkout__validation-message b2b-checkout__field-error";
        msgEl.setAttribute("role", "alert");
        input.after(msgEl);
      }
    }
    msgEl.innerHTML = this.escapeHtml(message);
    msgEl.style.display = "";
    input.setAttribute("aria-invalid", "true");

    clearTimeout(input._validationTimeout);
    if (!persistent) {
      input._validationTimeout = setTimeout(() => {
        this.clearInputValidationMessage(input);
      }, 3000);
    }
  }

  /**
   * Clear a dynamic validation message from a checkout input field.
   */
  clearInputValidationMessage(input) {
    if (!input) return;
    clearTimeout(input._validationTimeout);
    const field = input.closest(".b2b-checkout__field");
    if (!field) return;

    // Check for row-level message first, then inline
    let msgEl = null;
    const row = input.closest(".b2b-checkout__field-row");
    if (row && input.id) {
      msgEl = row.parentNode.querySelector(`#${CSS.escape(input.id + "-validation")}`);
    }
    if (!msgEl) {
      msgEl = field.querySelector(".b2b-checkout__validation-message");
    }
    if (msgEl) {
      msgEl.style.display = "none";
    }
    // Restore any hidden field error
    const existingError = field.querySelector(".b2b-checkout__field-error:not(.b2b-checkout__validation-message)");
    if (existingError) {
      existingError.style.display = "";
    }
    input.setAttribute("aria-invalid", "false");
  }

  /**
   * Bind shipping address toggle
   */
  bindShippingAddressToggle() {
    const radios = document.querySelectorAll('input[name="shipping_address"]');
    const currentAddressDetails = document.getElementById(
      "current-address-details",
    );
    const newAddressForm = document.getElementById("new-address-form");
    const addressbookSection = document.getElementById("addressbook-section");

    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        // Hide all sections first
        if (currentAddressDetails) currentAddressDetails.style.display = "none";
        if (newAddressForm) newAddressForm.style.display = "none";
        if (addressbookSection) addressbookSection.style.display = "none";

        // Clear new-address field errors when switching away
        if (radio.value !== "new") {
          this.clearShippingAddressFieldErrors();
        }

        // Show the appropriate section
        if (radio.value === "current") {
          if (currentAddressDetails)
            currentAddressDetails.style.display = "block";
        } else if (radio.value === "new") {
          if (newAddressForm) newAddressForm.style.display = "block";
        } else if (radio.value === "addressbook") {
          if (addressbookSection) addressbookSection.style.display = "block";
        }
      });
    });
  }

  /**
   * Bind shipping method toggle
   */
  bindShippingMethodToggle() {
    const radios = document.querySelectorAll('input[name="shipping_method"]');
    const carrierOptions = document.getElementById("carrier-options");

    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        if (radio.value === "select_carrier") {
          if (carrierOptions) carrierOptions.style.display = "block";
          this.groupRatesByCarrier();
          this.populateCarrierDropdown();
          this.toggleCarrierDependentFields();
        } else {
          this.clearCarrierSelections();
          if (carrierOptions) carrierOptions.style.display = "none";
        }
      });
    });

    // Carrier native &lt;select&gt;: populate service options and clear errors
    const carrierSelectEl = document.getElementById("carrier-select");
    if (carrierSelectEl) {
      carrierSelectEl.addEventListener("change", () => {
        const selectedValue = carrierSelectEl.value;
        const carrierWrap = document.getElementById("carrier-float-wrap");
        if (carrierWrap)
          carrierWrap.classList.toggle("has-value", !!selectedValue);
        const carriers = Object.keys(this.carrierGroups);
        const carrierName = carriers.find(
          (c) => c.toLowerCase().replace(/\s+/g, "-") === selectedValue,
        );
        if (carrierName) {
          this.populateServiceDropdown(carrierName);
        }
        this.clearFieldError("carrier-select");
        this.toggleCarrierDependentFields();
      });
    }

    // When freight term changes, hide/show carrier account field (hide when "Add shipping charges to my invoice" is selected)
    if (carrierOptions) {
      carrierOptions.addEventListener("change", (e) => {
        if (e.target.matches('input[name="freight_term"]')) {
          this.toggleCarrierDependentFields();
        }
      });
    }

  }

  /**
   * Initialize visible states based on default selections
   */
  initializeVisibleStates() {
    // Shipping address - show current address details by default, hide new address form and address book
    const currentAddressDetails = document.getElementById(
      "current-address-details",
    );
    const newAddressForm = document.getElementById("new-address-form");
    const addressbookSection = document.getElementById("addressbook-section");
    const shippingAddressRadio = document.querySelector(
      'input[name="shipping_address"]:checked',
    );

    // Hide all sections first
    if (currentAddressDetails) currentAddressDetails.style.display = "none";
    if (newAddressForm) newAddressForm.style.display = "none";
    if (addressbookSection) addressbookSection.style.display = "none";

    if (shippingAddressRadio) {
      if (shippingAddressRadio.value === "current") {
        if (currentAddressDetails)
          currentAddressDetails.style.display = "block";
      } else if (shippingAddressRadio.value === "new") {
        if (newAddressForm) newAddressForm.style.display = "block";
      } else if (shippingAddressRadio.value === "addressbook") {
        if (addressbookSection) addressbookSection.style.display = "block";
      }
    } else {
      // Default to showing current address
      if (currentAddressDetails) currentAddressDetails.style.display = "block";
    }

    // Shipping method - show carrier options if carrier is selected
    const carrierOptions = document.getElementById("carrier-options");
    const shippingMethodRadio = document.querySelector(
      'input[name="shipping_method"]:checked',
    );

    if (shippingMethodRadio) {
      if (shippingMethodRadio.value === "select_carrier") {
        if (carrierOptions) carrierOptions.style.display = "block";
      } else {
        if (carrierOptions) carrierOptions.style.display = "none";
      }
    }
  }

  /**
   * Bind shipping instructions toggle
   */
  bindShippingInstructionsToggle() {
    const toggle = document.getElementById("toggle-instructions");
    const section = document.getElementById("instructions-section");

    if (toggle && section) {
      toggle.addEventListener("click", () => {
        const isVisible = section.classList.contains("visible");
        section.classList.toggle("visible");

        // WCAG 2.2: expose expanded state to assistive tech
        toggle.setAttribute("aria-expanded", String(!isVisible));

        // Update button text/icon
        const svg = toggle.querySelector("svg");
        if (svg) {
          if (isVisible) {
            // Show plus icon
            svg.innerHTML =
              '<line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>';
          } else {
            // Show minus icon
            svg.innerHTML = '<line x1="5" y1="12" x2="19" y2="12"></line>';
          }
        }
      });
    }
  }

  /**
   * Bind review order button
   */
  async bindReviewOrderButton() {
    const reviewBtn = document.getElementById("review-order-btn");
    const reviewButtonDefaultText =
      document.getElementById("review-order-btn")?.textContent || "Continue";

    if (reviewBtn) {
      reviewBtn.addEventListener("click", async (e) => {
        e.preventDefault();

        if (!this.validateForm()) {
          return;
        }

        const shippingRadio = document.querySelector(
          'input[name="shipping_address"]:checked',
        );
        const isNewAddress = shippingRadio?.value === "new";

        // Optional: validate address via Google Address Validation API (backend proxy)
        if (isNewAddress) {
          reviewBtn.disabled = true;
          reviewBtn.textContent = window.__t('validating_address');
          try {
            const address = this.getNewAddressForValidation();
            const response = await this.validateAddressWithApi(address);
            const result = response?.result;
            const verdict = result?.verdict;
            const nextAction = verdict?.possibleNextAction;

            if (nextAction === "FIX") {
              reviewBtn.disabled = false;
              reviewBtn.textContent = reviewButtonDefaultText;
              this.clearShippingAddressFieldErrors();
              this.showFixErrors(result?.address);
              return;
            }

            if (nextAction === "CONFIRM_ADD_SUBPREMISES") {
              // If user already saw the subpremises suggestion and resubmitted, let them proceed
              if (this._subpremisesShown) {
                this._subpremisesShown = false;
                const validatedZip = this.getValidatedZip(result);
                if (validatedZip) {
                  const zipEl = document.getElementById("new-zip");
                  if (zipEl) zipEl.value = validatedZip;
                }
                // Fall through to proceed with the order
              } else {
                this._subpremisesShown = true;
                const suggested = this.parseValidatedAddress(
                  result?.address || {},
                );
                this.fillNewAddressForm(suggested);
                this.clearShippingAddressFieldErrors();
                this.setFieldError(
                  "new-address-2",
                  window.__t("address_validation_subpremise_needed"),
                );
                const addr2Field = document
                  .getElementById("new-address-2")
                  ?.closest(".b2b-checkout__field");
                if (addr2Field) {
                  addr2Field.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                document.getElementById("new-address-2")?.focus({ preventScroll: true });
                reviewBtn.disabled = false;
                reviewBtn.textContent = reviewButtonDefaultText;
                return;
              }
            }

            if (nextAction === "CONFIRM") {
              const suggested = this.parseValidatedAddress(
                result?.address || {},
              );
              const parts = [
                suggested.address1,
                suggested.address2,
                [suggested.city, suggested.state, suggested.zip]
                  .filter(Boolean)
                  .join(", "),
                suggested.countryCode,
              ].filter(Boolean);
              const formattedAddress = parts.join("\n");
              this.showAddressConfirmModal(
                formattedAddress,
                () => {
                  this.fillNewAddressForm(suggested);
                  reviewBtn.textContent = window.__t('processing');
                  this.proceedToReviewOrder().catch((err) => {
                    reviewBtn.disabled = false;
                    reviewBtn.textContent = reviewButtonDefaultText;
                    this.showMessage(
                      this.strings.error_try_again || "An error occurred. Please try again.",
                      "error",
                    );
                  });
                },
                () => {
                  reviewBtn.disabled = false;
                  reviewBtn.textContent = reviewButtonDefaultText;
                },
              );
              return;
            }

            // ACCEPT or no verdict: update ZIP with validated value (includes +4 suffix) and proceed
            const validatedZip = this.getValidatedZip(result);
            if (validatedZip) {
              const zipEl = document.getElementById("new-zip");
              if (zipEl) zipEl.value = validatedZip;
            }
          } catch (err) {
            reviewBtn.disabled = false;
            reviewBtn.textContent = reviewButtonDefaultText;
            this.clearShippingAddressFieldErrors();
            this.setFieldError(
              "new-address-1",
              this.strings.address_validation_error ||
                "Address validation failed. Please check the address.",
            );
            const addr1Field = document.getElementById("new-address-1")?.closest(".b2b-checkout__field");
            if (addr1Field) addr1Field.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
        }

        // Disable button during processing
        reviewBtn.disabled = true;
        reviewBtn.textContent = window.__t('processing');

        try {
          await this.proceedToReviewOrder();
        } catch (error) {
          reviewBtn.disabled = false;
          reviewBtn.textContent = reviewButtonDefaultText;
          this.showMessage(
            this.strings.error_try_again || "An error occurred. Please try again.",
            "error",
          );
        }
      });
    }
  }

  /**
   * Clear per-field error state and messages in the new shipping address form
   */
  clearShippingAddressFieldErrors() {
    const form = document.getElementById("new-address-form");
    if (!form) return;
    form
      .querySelectorAll(".b2b-checkout__field--invalid")
      .forEach((el) => el.classList.remove("b2b-checkout__field--invalid"));
    form
      .querySelectorAll(".b2b-checkout__field-error")
      .forEach((el) => el.remove());
    // Reset aria-invalid on all inputs
    form.querySelectorAll("[aria-invalid]").forEach((input) => {
      input.setAttribute("aria-invalid", "false");
      input.removeAttribute("aria-describedby");
    });
  }

  /**
   * Clear validation error for a field control (border, message, aria).
   * @param {HTMLElement} input - input, textarea, or select inside a `.b2b-checkout__field`
   */
  clearFieldErrorForElement(input) {
    if (!input) return;
    const field = input.closest(".b2b-checkout__field");
    if (!field) return;
    field.classList.remove("b2b-checkout__field--invalid");
    // Check for inline error
    const err = field.querySelector(".b2b-checkout__field-error");
    if (err) err.remove();
    // Check for row-level error (City/State/ZIP)
    if (input.id) {
      const rowErr = field.closest(".b2b-checkout__field-row")?.parentNode?.querySelector(`.b2b-checkout__field-error[data-for="${input.id}"]`);
      if (rowErr) rowErr.remove();
    }
    input.setAttribute("aria-invalid", "false");
    input.removeAttribute("aria-describedby");
  }

  /**
   * Clear validation error from a single field by input id
   * @param {string} inputId - id of the input (e.g. 'new-zip', 'new-phone')
   */
  clearFieldError(inputId) {
    this.clearFieldErrorForElement(document.getElementById(inputId));
  }

  /**
   * Clear PO Number field error (used before re-validating)
   */
  clearPoNumberFieldError() {
    this.clearFieldErrorForElement(document.getElementById("po-number"));
  }

  /**
   * Validate ZIP/postal format by country. US: 5 or 5+4 digits. CA: A1A 1A1. IT: 5 digits (CAP). MX: 5 digits.
   * @param {string} zip - trimmed value
   * @param {string} countryCode - 'US', 'CA', 'IT', or 'MX'
   * @returns {boolean}
   */
  isValidZip(zip, countryCode) {
    if (!zip) return false;
    const code = (countryCode).toUpperCase();
    if (code === "US") {
      return /^\d{5}(-\d{4})?$/.test(zip);
    }
    if (code === "CA") {
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(
        zip.replace(/\s+/g, " ").trim(),
      );
    }
    if (code === "IT") {
      return /^\d{5}$/.test(zip.trim());
    }
    if (code === "MX") {
      return /^\d{5}$/.test(zip.trim());
    }
    return true;
  }

  /**
   * Check if a US ZIP code prefix matches the selected state.
   */
  isZipMatchingState(zip, stateCode, countryCode) {
    if ((countryCode).toUpperCase() !== "US") return true;
    if (!zip || !stateCode) return true;
    const prefix = parseInt(zip.substring(0, 3), 10);
    if (isNaN(prefix)) return true;
    const zipStateMap = {
      AL: [[350, 369]], AK: [[995, 999]], AZ: [[850, 865]], AR: [[716, 729]],
      CA: [[900, 961]], CO: [[800, 816]], CT: [[60, 69]], DE: [[197, 199]],
      FL: [[320, 349]], GA: [[300, 319], [398, 399]], HI: [[967, 968]],
      ID: [[832, 838]], IL: [[600, 629]], IN: [[460, 479]], IA: [[500, 528]],
      KS: [[660, 679]], KY: [[400, 427]], LA: [[700, 714]], ME: [[39, 49]],
      MD: [[206, 219]], MA: [[10, 27], [55, 55]], MI: [[480, 499]],
      MN: [[550, 567]], MS: [[386, 397]], MO: [[630, 658]], MT: [[590, 599]],
      NE: [[680, 693]], NV: [[889, 898]], NH: [[30, 38]], NJ: [[70, 89]],
      NM: [[870, 884]], NY: [[100, 149], [90, 98]], NC: [[270, 289]],
      ND: [[580, 588]], OH: [[430, 459]], OK: [[730, 749]], OR: [[970, 979]],
      PA: [[150, 196]], RI: [[28, 29]], SC: [[290, 299]], SD: [[570, 577]],
      TN: [[370, 385]], TX: [[750, 799], [885, 885]], UT: [[840, 847]],
      VT: [[50, 59]], VA: [[220, 246]], WA: [[980, 994]], WV: [[247, 268]],
      WI: [[530, 549]], WY: [[820, 831]], DC: [[200, 205]],
      PR: [[6, 9], [900, 988]], VI: [[8, 8]], GU: [[969, 969]],
    };
    const ranges = zipStateMap[stateCode.toUpperCase()];
    if (!ranges) return true;
    return ranges.some(([min, max]) => prefix >= min && prefix <= max);
  }

  /**
   * Validate phone: at least 10 digits (11 allowed if leading 1 for US/CA).
   * @param {string} phone - raw input
   * @returns {boolean}
   */
  isValidPhone(phone) {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, "");
    return (
      digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
    );
  }

  /**
   * Get new-address form values for address validation API
   * @returns {{ address1: string, address2: string, city: string, state: string, zip: string, country: string }}
   */
  getNewAddressForValidation() {
    return {
      address1: document.getElementById("new-address-1")?.value?.trim() || "",
      address2: document.getElementById("new-address-2")?.value?.trim() || "",
      city: document.getElementById("new-city")?.value?.trim() || "",
      state: document.getElementById("new-state")?.value?.trim() || "",
      zip: document.getElementById("new-zip")?.value?.trim() || "",
      country: document.getElementById("new-country")?.value?.trim() || "",
    };
  }

  /**
   * Validate address via the backend address validation endpoint (Google Address Validation API).
   * Returns the full Google Address Validation API response.
   * @param {{ address1: string, address2: string, city: string, state: string, zip: string, country: string }} address
   * @returns {Promise<{ result?: { verdict?: { possibleNextAction?: string }, address?: object } }>}
   */
  async validateAddressWithApi(address) {
    const response = await fetch(`${GADGET_API_BASE}/api/customer/address-validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${window.myJWT || ""}`,
      },
      body: JSON.stringify({
        addressLine1: address.address1 || "",
        addressLine2: address.address2 || "",
        countryCode: (address.country).trim() || "",
        city: address.city || "",
        stateCode: address.state || "",
        zip: address.zip || "",
      }),
    });

    if (!response.ok) {
      throw new Error(`Address validation request failed with status ${response.status}`);
    }

    return response.json();
  }

  /**
   * Map Google Address Validation API result.address to our form shape.
   * Supports both the full Google response shape (postalAddress nested) and flat shape.
   * @param {object} addr
   * @returns {{ address1: string, address2: string, city: string, state: string, zip: string, countryCode: string }}
   */
  parseValidatedAddress(addr) {
    if (!addr) return { address1: "", address2: "", city: "", state: "", zip: "", countryCode: "" };
    const postal = addr?.postalAddress || addr;
    const lines = postal.addressLines || [];
    const address1 = lines[0] || "";
    const address2 = lines[1] || "";
    let state = postal.administrativeArea || "";
    const regionCode = postal.regionCode || "";
    if (state && regionCode === "US" && state.length > 2) {
      state = US_STATE_NAME_TO_CODE[state] || state;
    }
    return {
      address1,
      address2,
      city: postal.locality || "",
      state,
      zip: postal.postalCode || "",
      countryCode: regionCode,
    };
  }

  /**
   * Extract the full ZIP code (with +4 suffix when available) from the validation result.
   * @param {{ address?: { postalAddress?: object, addressComponents?: object[] } }} result
   * @returns {string|null}
   */
  getValidatedZip(result) {
    const postal = result?.address?.postalAddress;
    if (postal?.postalCode) return postal.postalCode;

    const components = result?.address?.addressComponents || [];
    const postalCode = components.find(c => c.componentType === "postal_code")?.componentName?.text || "";
    const suffix = components.find(c => c.componentType === "postal_code_suffix")?.componentName?.text || "";
    if (postalCode && suffix) return `${postalCode}-${suffix}`;
    return postalCode || null;
  }

  /**
   * Fill the new-address form with validated address values
   */
  fillNewAddressForm(validated) {
    const set = (id, value) => {
      const el = document.getElementById(id);
      if (el) el.value = value || "";
    };
    set("new-address-1", validated.address1);
    set("new-address-2", validated.address2);
    set("new-city", validated.city);
    set("new-state", validated.state);
    set("new-zip", validated.zip);
    set("new-country", validated.countryCode);
  }

  /**
   * Map Google Address Validation component types to checkout form input IDs.
   */
  getFieldIdForComponentType(componentType) {
    const mapping = {
      street_number: "new-address-1",
      route: "new-address-1",
      subpremise: "new-address-2",
      locality: "new-city",
      administrative_area_level_1: "new-state",
      postal_code: "new-zip",
      postal_code_suffix: "new-zip",
    };
    return mapping[componentType] || null;
  }

  /**
   * Map Google Address Validation component types to user-friendly field labels.
   */
  getLabelForComponentType(componentType) {
    const keyMap = {
      street_number: "address_validation_label_street_number",
      route: "address_validation_label_route",
      subpremise: "address_validation_label_subpremise",
      locality: "address_validation_label_locality",
      administrative_area_level_1: "address_validation_label_state",
      postal_code: "address_validation_label_postal_code",
      postal_code_suffix: "address_validation_label_postal_code",
    };
    const key = keyMap[componentType];
    return key ? window.__t(key) : componentType;
  }

  /**
   * Show specific field-level errors based on unconfirmed or missing address components from Google.
   * Falls back to a generic error on Address Line 1 if no specific components are identified.
   * @param {{ addressComponents?: object[], missingComponentTypes?: string[], unconfirmedComponentTypes?: string[] }} address
   */
  showFixErrors(address) {
    const components = address?.addressComponents || [];
    const suspicious = components.filter(
      c => c.confirmationLevel === "UNCONFIRMED_AND_SUSPICIOUS" || c.confirmationLevel === "UNCONFIRMED_BUT_PLAUSIBLE"
    );
    const missingTypes = address?.missingComponentTypes || [];

    const erroredFields = new Set();
    let firstField = null;

    const addFieldError = (fieldId, message) => {
      if (!fieldId || erroredFields.has(fieldId)) return;
      erroredFields.add(fieldId);
      this.setFieldError(fieldId, message);
      if (!firstField) firstField = fieldId;
    };

    for (const componentType of missingTypes) {
      const fieldId = this.getFieldIdForComponentType(componentType);
      const message = fieldId === "new-address-1"
        ? window.__t("address_validation_address_unverified")
        : window.__t("address_validation_component_missing", {
            label: this.getLabelForComponentType(componentType),
          });
      addFieldError(fieldId, message);
    }

    for (const component of suspicious) {
      const fieldId = this.getFieldIdForComponentType(component.componentType);
      if (!fieldId) continue;

      const label = this.getLabelForComponentType(component.componentType);
      const message = component.confirmationLevel === "UNCONFIRMED_AND_SUSPICIOUS"
        ? window.__t("address_validation_component_unverified", { label })
        : window.__t("address_validation_component_unconfirmed", { label });

      addFieldError(fieldId, message);
    }

    if (erroredFields.size === 0) {
      this.setFieldError(
        "new-address-1",
        this.strings.address_validation_fix_message ||
          window.__t("address_validation_fix_message"),
      );
      firstField = "new-address-1";
    }

    // Scroll to the first errored field
    if (firstField) {
      const field = document.getElementById(firstField)?.closest(".b2b-checkout__field");
      if (field) {
        field.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  /**
   * Show modal asking user to confirm or edit suggested address
   * @param {string} formattedAddress - single line or multi-line display string
   * @param {() => void} onUse - called when user clicks "Use this address"
   * @param {() => void} onEdit - called when user clicks "Edit address"
   */
  showAddressConfirmModal(formattedAddress, onUse, onEdit) {
    const existing = document.getElementById("b2b-address-validation-modal");
    if (existing) existing.remove();

    const title =
      this.strings.address_validation_confirm_title || "Confirm address";
    const useText =
      this.strings.address_validation_use_address || "Use this address";
    const editText =
      this.strings.address_validation_edit_address || "Edit address";

    const overlay = document.createElement("div");
    overlay.id = "b2b-address-validation-modal";
    overlay.className = "b2b-checkout__address-validation-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "b2b-address-validation-modal-title");
    overlay.innerHTML = `
      <div class="b2b-checkout__address-validation-modal">
        <h2 id="b2b-address-validation-modal-title" class="b2b-checkout__address-validation-modal-title">${this.escapeHtml(title)}</h2>
        <p class="b2b-checkout__address-validation-modal-address">${this.escapeHtml(formattedAddress)}</p>
        <div class="b2b-checkout__address-validation-modal-actions">
          <button type="button" class="rs-button rs-button-primary rs-button-medium b2b-checkout__address-validation-use-btn">${this.escapeHtml(useText)}</button>
          <button type="button" class="rs-button rs-button-secondary rs-button-medium b2b-checkout__address-validation-edit-btn">${this.escapeHtml(editText)}</button>
        </div>
      </div>
    `;

    const triggerElement = document.activeElement;

    const close = () => {
      document.removeEventListener("keydown", onKeyDown);
      overlay.remove();
      document.body.style.overflow = "";
      if (triggerElement instanceof HTMLElement) {
        triggerElement.focus();
      }
    };

    // Focus trap: keep Tab/Shift+Tab within the modal
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        close();
        onEdit();
        return;
      }
      if (e.key === "Tab") {
        const focusable = overlay.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    overlay.querySelector(".b2b-checkout__address-validation-use-btn").addEventListener("click", () => {
      close();
      onUse();
    });
    overlay.querySelector(".b2b-checkout__address-validation-edit-btn").addEventListener("click", () => {
      close();
      onEdit();
    });
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        close();
        onEdit();
      }
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    // Focus the first button in the modal
    const firstBtn = overlay.querySelector("button");
    if (firstBtn) firstBtn.focus();
  }

  /**
   * Proceed to review order: collect form data, save address book if needed, save cart, redirect
   */
  async proceedToReviewOrder() {
    const formData = await this.collectFormData();
    if (
      formData.shipping_address.type === "new" &&
      formData.shipping_address.save_to_addressbook
    ) {
      await this.saveToPersonalAddressBook({
        label: formData.shipping_address.company,
        attention: formData.shipping_address.attention,
        address1: formData.shipping_address.address1,
        address2: formData.shipping_address.address2,
        city: formData.shipping_address.city,
        state: formData.shipping_address.state,
        zip: formData.shipping_address.zip,
        country: formData.shipping_address.country,
        phone: formData.shipping_address.phone,
      });
    }
    await this.saveCartAttributes(formData);
    sessionStorage.setItem("b2b_checkout_data", JSON.stringify(formData));
    window.location.href = "/pages/review-order";
  }

  /**
   * SVG icon for field validation errors
   */
  getFieldErrorIconSvg() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" class="b2b-checkout__field-error-icon"><path d="M10 10H0V0H10V10ZM5.0293 6.73633C4.84795 6.73633 4.70577 6.78949 4.60254 6.89551C4.50215 6.99873 4.45215 7.12686 4.45215 7.28027C4.45215 7.42815 4.50209 7.55511 4.60254 7.66113C4.70577 7.76432 4.84798 7.8164 5.0293 7.81641C5.20787 7.81641 5.34891 7.76437 5.45215 7.66113C5.55535 7.55512 5.60644 7.42812 5.60645 7.28027C5.60645 7.12691 5.55525 6.99871 5.45215 6.89551C5.34891 6.78948 5.20787 6.73633 5.0293 6.73633ZM4.48145 1.66406L4.58594 5.96191H5.44824L5.54785 1.66406H4.48145Z" fill="#D22730"/></svg>';
  }

  /**
   * Show error on a single field (red border + message below with icon).
   * For fields inside a field-row (City/State/ZIP), the error is placed after the
   * row as a full-width element so it isn't crammed into a narrow column on desktop.
   * @param {string} inputId - id of the input/select (e.g. 'new-business-name', 'billing-zip')
   * @param {string} message - error text (e.g. 'Enter a company name.')
   */
  setFieldError(inputId, message) {
    const input = document.getElementById(inputId);
    if (!input) return;
    const field = input.closest(".b2b-checkout__field");
    if (!field) return;
    field.classList.add("b2b-checkout__field--invalid");
    const errorId = inputId + "-error";

    // Check if this field is inside the City/State/ZIP row
    const row = input.closest(".b2b-checkout__field-row");

    if (row) {
      // Place the error after the row as a full-width sibling
      let errEl = row.parentNode.querySelector(`#${CSS.escape(errorId)}`);
      if (!errEl) {
        errEl = document.createElement("span");
        errEl.className = "b2b-checkout__field-error b2b-checkout__field-error--row-level";
        errEl.id = errorId;
        errEl.setAttribute("role", "alert");
        errEl.setAttribute("data-for", inputId);
        row.insertAdjacentElement("afterend", errEl);
      }
      errEl.innerHTML = this.getFieldErrorIconSvg() + this.escapeHtml(message);
    } else {
      // Place the error after the float-wrap (not inside it) to avoid affecting label centering
      let errEl = field.querySelector(".b2b-checkout__field-error");
      const insertTarget =
        input.closest(".b2b-checkout__input-float-wrap") ||
        input.closest(".b2b-checkout__select-float-wrap") ||
        input.closest(".b2b-checkout__phone-with-tooltip") ||
        input.closest(".b2b-checkout__account-with-tooltip") ||
        input;
      if (!errEl) {
        errEl = document.createElement("span");
        errEl.className = "b2b-checkout__field-error";
        errEl.id = errorId;
        errEl.setAttribute("role", "alert");
        insertTarget.after(errEl);
      } else if (
        insertTarget !== input &&
        insertTarget.nextElementSibling !== errEl
      ) {
        insertTarget.after(errEl);
      }
      errEl.id = errorId;
      errEl.innerHTML = this.getFieldErrorIconSvg() + this.escapeHtml(message);
    }
    input.setAttribute("aria-invalid", "true");
    input.setAttribute("aria-describedby", errorId);
  }

  setShippingAddressFieldError(inputId, message) {
    this.setFieldError(inputId, message);
  }

  /**
   * Set validation error on a combobox field (error message appears below the dropdown)
   * @param {string} triggerInputId - id of the combobox trigger input
   * @param {string} comboboxContainerId - id of the combobox wrapper div (to insert error after)
   * @param {string} message - error text
   */
  setComboboxFieldError(triggerInputId, comboboxContainerId, message) {
    const trigger = document.getElementById(triggerInputId);
    const combobox = document.getElementById(comboboxContainerId);
    if (!trigger || !combobox) return;
    const field = trigger.closest(".b2b-checkout__field");
    if (!field) return;
    field.classList.add("b2b-checkout__field--invalid");
    const errorId = triggerInputId + "-error";
    const insertAfter =
      trigger.closest(".b2b-checkout__select-float-wrap") || combobox;
    let errEl = field.querySelector(".b2b-checkout__field-error");
    if (!errEl) {
      errEl = document.createElement("span");
      errEl.className = "b2b-checkout__field-error";
      errEl.id = errorId;
      errEl.setAttribute("role", "alert");
      insertAfter.after(errEl);
    } else if (insertAfter.nextElementSibling !== errEl) {
      insertAfter.after(errEl);
    }
    errEl.id = errorId;
    errEl.innerHTML = this.escapeHtml(message);
    trigger.setAttribute("aria-invalid", "true");
    trigger.setAttribute("aria-describedby", errorId);
  }

  /**
   * Clear validation error from a single combobox field (e.g. address book, billing)
   */
  clearComboboxFieldError(triggerInputId) {
    const trigger = document.getElementById(triggerInputId);
    if (!trigger) return;
    const field = trigger.closest(".b2b-checkout__field");
    if (!field) return;
    field.classList.remove("b2b-checkout__field--invalid");
    const err = field.querySelector(".b2b-checkout__field-error");
    if (err) err.remove();
    trigger.removeAttribute("aria-invalid");
    trigger.removeAttribute("aria-describedby");
  }

  /**
   * Clear validation errors from carrier/service/account fields
   */
  clearCarrierFieldErrors() {
    this.clearFieldErrorForElement(document.getElementById("carrier-select"));
    this.clearFieldErrorForElement(document.getElementById("service-select"));
    this.clearFieldErrorForElement(document.getElementById("carrier-account"));
  }

  /**
   * Validate the checkout form
   */
  validateForm() {
    let isValid = true;
    const errors = [];

    // Check if cart has items
    if (!this.cart || !this.cart.items.length) {
      // Redirect to the cart page
      window.location.href = "/cart";
    }

    // Check shipping address selection
    const shippingAddressRadio = document.querySelector(
      'input[name="shipping_address"]:checked',
    );
    if (!shippingAddressRadio) {
      errors.push("Please select a shipping address");
      isValid = false;
    } else if (shippingAddressRadio.value === "addressbook") {
      this.clearFieldError("addressbook-select");
      if (!this.selectedAddressBookAddress) {
        this.setFieldError(
          "addressbook-select",
          "Please select an address from your address book",
        );
        errors.push("Please select an address from your address book");
        isValid = false;
      }
    } else if (shippingAddressRadio.value === "new") {
      this.clearShippingAddressFieldErrors();
      const businessName = document
        .getElementById("new-business-name")
        ?.value?.trim();
      const country = document.getElementById("new-country")?.value?.trim();
      const address1 = document.getElementById("new-address-1")?.value?.trim();
      const city = document.getElementById("new-city")?.value?.trim();
      const state = document.getElementById("new-state")?.value?.trim();
      const zip = document.getElementById("new-zip")?.value?.trim();
      const phone = document.getElementById("new-phone")?.value?.trim();

      let firstNewAddressErrorEl = null;

      if (!businessName) {
        this.setShippingAddressFieldError(
          "new-business-name",
          "Enter a company name.",
        );
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-business-name");
        isValid = false;
      }
      if (!country) {
        this.setShippingAddressFieldError(
          "new-country",
          "Please select a country.",
        );
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-country");
        isValid = false;
      }
      if (!address1) {
        this.setShippingAddressFieldError(
          "new-address-1",
          "Enter a street address.",
        );
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-address-1");
        isValid = false;
      }
      if (!city) {
        this.setShippingAddressFieldError("new-city", "Enter a city.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-city");
        isValid = false;
      }
      const stateRequired = this.checkoutCountryHasStateOptions(country);
      if (stateRequired && !state) {
        this.setShippingAddressFieldError("new-state", "Select a state.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-state");
        isValid = false;
      }
      if (!zip) {
        this.setShippingAddressFieldError("new-zip", "Enter a ZIP code.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-zip");
        isValid = false;
      } else if (!this.isValidZip(zip, country)) {
        this.setShippingAddressFieldError("new-zip", "Enter a valid ZIP code.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-zip");
        isValid = false;
      } else if (!this.isZipMatchingState(zip, state, country)) {
        this.setShippingAddressFieldError("new-zip", "ZIP code does not match the selected state.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-zip");
        isValid = false;
      }
      if (!phone) {
        this.setShippingAddressFieldError("new-phone", "Enter a phone number.");
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-phone");
        isValid = false;
      } else if (!this.isValidPhone(phone)) {
        this.setShippingAddressFieldError(
          "new-phone",
          "Enter a valid phone number.",
        );
        if (!firstNewAddressErrorEl)
          firstNewAddressErrorEl = document.getElementById("new-phone");
        isValid = false;
      }

      if (firstNewAddressErrorEl) {
        firstNewAddressErrorEl.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        firstNewAddressErrorEl.focus({ preventScroll: true });
      }
    }

    // If "Select a carrier" is chosen, validate carrier, service, and account number
    let hasShippingMethodErrors = false;
    const shippingMethodRadio = document.querySelector(
      'input[name="shipping_method"]:checked',
    );
    if (shippingMethodRadio?.value === "select_carrier") {
      this.clearCarrierFieldErrors();
      const carrierValue = document
        .getElementById("carrier-select")
        ?.value?.trim();
      const serviceValue = document
        .getElementById("service-select")
        ?.value?.trim();
      const accountValue = document
        .getElementById("carrier-account")
        ?.value?.trim();

      if (!carrierValue) {
        this.setFieldError("carrier-select", "Select a carrier");
        isValid = false;
        hasShippingMethodErrors = true;
      }
      if (!serviceValue) {
        this.setFieldError("service-select", "Select shipping options");
        isValid = false;
        hasShippingMethodErrors = true;
      }
      const invoiceOptionChecked = !!document.querySelector(
        'input[name="freight_term"]:checked[data-invoice-option="true"]',
      );
      const singleFreightTerm = document.querySelector(
        'input[name="freight_term"][data-single-freight-term="true"]',
      );
      const requiresAccount =
        serviceValue && (singleFreightTerm || !invoiceOptionChecked);
      if (requiresAccount && !accountValue) {
        this.setFieldError("carrier-account", "Enter account number");
        isValid = false;
        hasShippingMethodErrors = true;
      }
      if (hasShippingMethodErrors) {
        const carrierOptions = document.getElementById("carrier-options");
        const firstError = carrierOptions?.querySelector(
          ".b2b-checkout__field-error",
        );
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }

    // Validate PO number (top-of-page field)
    this.clearPoNumberFieldError();
    const poInput = document.querySelector('input[name="po_number"]');
    if (poInput && !poInput.value.trim()) {
      this.setFieldError(
        "po-number",
        this.strings.error_po_required || "Enter PO number.",
      );
      isValid = false;
      poInput.scrollIntoView({ behavior: "smooth", block: "center" });
      poInput.focus({ preventScroll: true });
    }

    if (!isValid) {
      if (errors.length) {
        this.showMessage(errors.join(". "), "error");
      }
    }

    return isValid;
  }

  /**
   * Collect form data for submission
   */
  collectFormData() {
    // Bill to account (for current address)
    // REMOVE SPLIT ONCE RESIDEO UPDATES DATA
    const billToAccount = document
      .getElementById("address-line")
      ?.getAttribute("data-bill-to-account");
    const billToAccountId =
      billToAccount && billToAccount.indexOf("-") > 0
        ? billToAccount.split("-")[1]
        : billToAccount;

    const formData = {
      cart: this.cart,
      shipping_address: {},
      shipping_method: {},
      payment_method: {},
      bill_to_account: billToAccountId,
    };

    // Shipping address - use IDs from the HTML
    const shippingRadio = document.querySelector(
      'input[name="shipping_address"]:checked',
    );
    formData.shipping_address.type = shippingRadio?.value || "current";

    // Attention field (for current address)
    formData.shipping_address.attention =
      document.getElementById("attention-field")?.value || "";

    if (formData.shipping_address.type === "new") {
      // Get values from actual form field IDs
      formData.shipping_address.company =
        document.getElementById("new-business-name")?.value || "";
      formData.shipping_address.attention =
        document.getElementById("new-attention")?.value || "";
      formData.shipping_address.address1 =
        document.getElementById("new-address-1")?.value || "";
      formData.shipping_address.address2 =
        document.getElementById("new-address-2")?.value || "";
      formData.shipping_address.city =
        document.getElementById("new-city")?.value || "";
      formData.shipping_address.state =
        document.getElementById("new-state")?.value || "";
      formData.shipping_address.zip =
        document.getElementById("new-zip")?.value || "";
      formData.shipping_address.phone =
        document.getElementById("new-phone")?.value || "";
      formData.shipping_address.country =
        document.getElementById("new-country")?.value || "";
      formData.shipping_address.save_to_addressbook =
        document.getElementById("save-to-addressbook")?.checked || false;
    } else if (formData.shipping_address.type === "addressbook") {
      // Use selected address from address book
      formData.shipping_address.attention =
        document.getElementById("addressbook-attention")?.value || "";
      if (this.selectedAddressBookAddress) {
        formData.shipping_address.address1 =
          this.selectedAddressBookAddress.address1 || "";
        formData.shipping_address.address2 =
          this.selectedAddressBookAddress.address2 || "";
        formData.shipping_address.city =
          this.selectedAddressBookAddress.city || "";
        formData.shipping_address.state =
          this.selectedAddressBookAddress.provinceCode || "";
        formData.shipping_address.zip =
          this.selectedAddressBookAddress.zip || "";
        formData.shipping_address.country =
          this.selectedAddressBookAddress.countryCode || "";
        formData.shipping_address.firstName =
          this.selectedAddressBookAddress.firstName || "";
        formData.shipping_address.lastName =
          this.selectedAddressBookAddress.lastName || "";
        formData.shipping_address.addressbook_id =
          this.selectedAddressBookAddress.id;
        formData.shipping_address.label =
          this.selectedAddressBookAddress.label || "";
        formData.shipping_address.billing_address_external_id =
          this.selectedAddressBookAddress.billing_address_external_id || "";
      } else {
        console.warn('[b2b-checkout] addressbook type selected but selectedAddressBookAddress is null — shipping address fields will be empty');
      }
    } else {
      // Use current address from displayed company name/address
      formData.shipping_address.company =
        document.getElementById("company-name")?.textContent || "";
      formData.shipping_address.address_display =
        document.getElementById("address-line")?.textContent || "";
      formData.shipping_address.address1 =
        document
          .getElementById("address-line")
          ?.getAttribute("data-address1") || "";
      formData.shipping_address.city =
        document.getElementById("address-line")?.getAttribute("data-city") ||
        "";
      formData.shipping_address.state =
        document
          .getElementById("address-line")
          ?.getAttribute("data-provinceCode") || "";
      formData.shipping_address.zip =
        document.getElementById("address-line")?.getAttribute("data-zip") || "";
      formData.shipping_address.country =
        document
          .getElementById("address-line")
          ?.getAttribute("data-countryCode") || "";
      formData.shipping_address.addressbook_id =
        document
          .getElementById("address-line")
          ?.getAttribute("data-addressbookId") || "";
      formData.shipping_address.billing_address_external_id =
        document
          .getElementById("address-line")
          ?.getAttribute("data-billing-address-external-id") || "";
    }

    const salesAreaData = document.querySelector('input[name="attributes[sales_area_data]"]')?.getAttribute('value');
    const salesAreaDataObj = salesAreaData ? JSON.parse(salesAreaData) : '';
    console.log(salesAreaDataObj);

    // Shipping method - use IDs from the HTML
    const shippingMethodRadio = document.querySelector(
      'input[name="shipping_method"]:checked',
    );
    formData.shipping_method.type = shippingMethodRadio?.value || salesAreaDataObj[0]?.default_shipping_condition?.name;

    const defaultShippingConditionInput = document.querySelector(
      ".company-default-shipping-condition",
    );
    const defaultShippingConditionId =
      defaultShippingConditionInput?.value?.trim() || "";

    if (formData.shipping_method.type === "01") {
      formData.shipping_method.shipping_condition_id =
        defaultShippingConditionId;
      formData.shipping_method.carrier_code = "";
      formData.shipping_method.carrier_description = "";
      formData.shipping_method.shipping_condition_code =
        defaultShippingConditionId;
      formData.shipping_method.shipping_condition_description = "";
      formData.shipping_method.freight_term_code = "";
      formData.shipping_method.freight_term_description = "";
      formData.shipping_method.account_number = "";
    } else if (formData.shipping_method.type === "select_carrier") {
      formData.shipping_method.carrier =
        document.getElementById("carrier-select")?.value || "";
      formData.shipping_method.service =
        document.getElementById("service-select")?.value || "";
      formData.shipping_method.account_number =
        document.getElementById("carrier-account")?.value?.trim() || "";
      const freightTermInput =
        document.querySelector('input[name="freight_term"]:checked') ||
        document.querySelector(
          'input[name="freight_term"][data-single-freight-term="true"]',
        );
      formData.shipping_method.freight_term = freightTermInput?.value || "";
      const selectedRate = this.getSelectedRate();
      formData.shipping_method.shipping_condition_id =
        selectedRate?.handle || salesAreaDataObj[0]?.default_shipping_condition?.code;
      formData.shipping_method.carrier_external_id =
        selectedRate?.carrierExternalId || "";
      formData.shipping_method.carrier_code =
        selectedRate?.carrierCode ?? selectedRate?.carrierExternalId ?? "";
      formData.shipping_method.carrier_description =
        selectedRate?.carrierDescription ?? "";
      formData.shipping_method.shipping_condition_code =
        selectedRate?.shippingConditionCode ?? selectedRate?.handle ?? "";
      formData.shipping_method.shipping_condition_description =
        selectedRate?.shippingConditionDescription ?? selectedRate?.title ?? "";
      const selectedFreightTermObj = this.getSelectedFreightTerm(
        selectedRate,
        formData.shipping_method.freight_term,
      );
      formData.shipping_method.freight_term_code =
        selectedFreightTermObj?.code ?? formData.shipping_method.freight_term;
      formData.shipping_method.freight_term_description =
        selectedFreightTermObj?.description ?? "";
    }

    // Shipping instructions and packing list notes
    formData.shipping_method.instructions =
      document.getElementById("shipping-instructions")?.value || "";
    formData.shipping_method.packing_list_notes =
      document.getElementById("packing-list-notes")?.value || "";

    // Payment: PO only (from top-of-page field)
    formData.payment_method = {
      type: "purchase_order",
      po_number: document.getElementById("po-number")?.value?.trim() || "",
    };

    const addressCurrentChecked =
      document.getElementById("address-current")?.checked === true;
    formData.billing_same_as_shipping = addressCurrentChecked;
    formData.billing_address = addressCurrentChecked
      ? {
          ...formData.shipping_address,
          type: "same_as_shipping",
        }
      : {};

    formData.sales_area =
      document
        .getElementById("address-line")
        ?.getAttribute("data-sales-area") || "";

    return formData;
  }

  /**
   * Save checkout data to cart attributes for Shopify checkout prefill
   */
  async saveCartAttributes(formData) {
    const salesAreaData = document.querySelector('input[name="attributes[sales_area_data]"]')?.getAttribute('value');
    const salesAreaDataObj = salesAreaData ? JSON.parse(salesAreaData) : {};

    const attributes = {
      _po_number: formData.payment_method?.po_number || "",
      _sales_area: formData.sales_area || formData.sales_area_id || "",

      // Shipping method
      _shipping_method_type: formData.shipping_method.type || "best_way",
      _shipping_carrier: formData.shipping_method.carrier_external_id || "",
      _shipping_service: formData.shipping_method.service || "",
      _shipping_account_number: formData.shipping_method.account_number || "",
      _b2b_shipping_instructions: formData.shipping_method.instructions || "",
      _b2b_packing_list_notes:
        formData.shipping_method.packing_list_notes || "",

      // Freight/shipping for Informatica: shippingCondition, carrierExternalId, shipping_condition, freightTerm, customerCarrierAccountNumber
      _shipping_condition: formData.shipping_method.shipping_condition_id || salesAreaDataObj[0]?.default_shipping_condition?.code,
      _carrier_external_id: formData.shipping_method.carrier_external_id || "",
      _freight_term: formData.shipping_method.freight_term || "",
      _customer_carrier_account_number:
        formData.shipping_method.account_number || "",

      // Acceptance criteria: Shipping Method persisted fields (code + description + account)
      _checkout_shipping_method_carrier_code:
        formData.shipping_method.carrier_code ?? "",
      _checkout_shipping_method_carrier_description:
        formData.shipping_method.carrier_description ?? "",
      _checkout_shipping_method_shipping_condition_description:
        formData.shipping_method.shipping_condition_description ?? salesAreaDataObj[0]?.default_shipping_condition?.name,
      _checkout_shipping_method_freight_term_description:
        formData.shipping_method.freight_term_description ?? "",
      _checkout_shipping_method_account_number:
        formData.shipping_method.account_number ?? "",

      // Shipping address type
      _shipping_address_type: formData.shipping_address.type || "current",
      _attention: formData.shipping_address.attention || "",

      // Flat shipping address keys — the order-history/SAP sync
      // (/api/orders/sync-order-shipping-address-from-attributes) reads these, not the
      // JSON attribute, so the chosen custom address must be persisted flat on Confirm.
      _shipping_address1: formData.shipping_address.address1 || "",
      _shipping_address2: formData.shipping_address.address2 || "",
      _shipping_city: formData.shipping_address.city || "",
      _shipping_state: formData.shipping_address.state || "",
      _shipping_zip: formData.shipping_address.zip || "",
      _shipping_country: formData.shipping_address.country || "",
      _shipping_company:
        formData.shipping_address.company || formData.shipping_address.label || "",
      _shipping_first_name: formData.shipping_address.firstName || "",
      _shipping_last_name: formData.shipping_address.lastName || "",
      _shipping_phone: formData.shipping_address.phone || "",

      // Acceptance criteria: persisted shipping address data
      _checkout_shipping_instructions:
        formData.shipping_method.instructions || "",
      _checkout_packing_list_notes:
        formData.shipping_method.packing_list_notes || "",

      _b2b_bill_to_account_id: "",

      _billing_address_external_id:
        formData.shipping_address.billing_address_external_id || "",
    };

    // Billing same as shipping only applies when using the current company address
    if (document.getElementById("address-current")?.checked) {
      attributes._billing_same_as_shipping = "true";
      attributes._billing_address_type = "same_as_shipping";
    } else {
      attributes._billing_same_as_shipping = "false";
      attributes._billing_address_type = "";
    }

    // If using address book, also save the address book ID
    if (
      formData.shipping_address.type === "addressbook" &&
      formData.shipping_address.addressbook_id
    ) {
      attributes["_addressbook_id"] = formData.shipping_address.addressbook_id;
    }

    // Shipping address as JSON
    if (formData.shipping_address.type === "addressbook") {
      const shipTo = {
        type: formData.shipping_address.type,
        company:
          formData.shipping_address.company ||
          formData.shipping_address.label ||
          "",
        address1: formData.shipping_address.address1 || "",
        address2: formData.shipping_address.address2 || "",
        city: formData.shipping_address.city || "",
        state: formData.shipping_address.state || "",
        zip: formData.shipping_address.zip || "",
        country: formData.shipping_address.country || "",
        firstName: formData.shipping_address.firstName || "",
        lastName: formData.shipping_address.lastName || "",
      };
      if (formData.shipping_address.addressbook_id) {
        shipTo.addressbook_id = formData.shipping_address.addressbook_id;
      }
      attributes["_checkout_custom_shipping_address_json"] = JSON.stringify(shipTo);
    } else if (formData.shipping_address.type === "new") {
      const customAddress = {
        type: formData.shipping_address.type,
        company: formData.shipping_address.company || "",
        attention: formData.shipping_address.attention || "",
        address1: formData.shipping_address.address1 || "",
        address2: formData.shipping_address.address2 || "",
        city: formData.shipping_address.city || "",
        state: formData.shipping_address.state || "",
        zip: formData.shipping_address.zip || "",
        country: formData.shipping_address.country || "",
        phone: formData.shipping_address.phone || "",
        firstName: formData.shipping_address.firstName || "",
        lastName: formData.shipping_address.lastName || "",
        save_to_addressbook:
          formData.shipping_address.save_to_addressbook || false,
      };
      attributes["_checkout_custom_shipping_address_json"] =
        JSON.stringify(customAddress);
    } else {
      attributes["_checkout_custom_shipping_address_json"] = "";
    }

    try {
      console.log('[b2b-checkout] Saving cart attributes:', {
        shippingAddressType: formData.shipping_address.type,
        hasCustomAddressJson: !!attributes["_checkout_custom_shipping_address_json"],
        customAddressJson: attributes["_checkout_custom_shipping_address_json"] ? JSON.parse(attributes["_checkout_custom_shipping_address_json"]) : null,
        addressbookId: attributes["_addressbook_id"] || null,
      });
      // Update cart attributes
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[b2b-checkout] Failed to save cart attributes:', response.status, errText);
      } else {
        console.log('[b2b-checkout] Cart attributes saved successfully');
      }
    } catch (error) {
      console.error('[b2b-checkout] Error saving cart attributes:', error);
    }
  }

  /**
   * Show a message to the user
   */
  showMessage(message, type = "info") {
    // Remove existing messages
    const existingMessage = document.querySelector(".b2b-checkout__message");
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create message element (WCAG 2.2: live region so screen readers announce)
    const messageEl = document.createElement("div");
    messageEl.className = `b2b-checkout__message b2b-checkout__message--${type}`;
    messageEl.setAttribute("role", "alert");
    messageEl.setAttribute(
      "aria-live",
      type === "error" ? "assertive" : "polite",
    );
    messageEl.setAttribute("aria-atomic", "true");
    const closeLabel = this.strings.close_message || "Close message";
    messageEl.innerHTML = `
      <span>${message}</span>
      <button type="button" class="b2b-checkout__message-close" aria-label="${this.escapeHtml(closeLabel)}">&times;</button>
    `;

    // Insert where the user will see it
    const main = document.querySelector(".b2b-checkout__form");
    if (main) main.insertAdjacentElement("afterbegin", messageEl);
    if (type === "error" && messageEl.parentNode) {
      messageEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    // Bind close button
    const closeBtn = messageEl.querySelector(".b2b-checkout__message-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => messageEl.remove());
    }

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.remove();
      }
    }, 5000);
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window._b2bCheckoutInstance = new B2BCheckout();
});

/**
 * When currency changes externally (e.g., rs-currency-switch), update
 * the checkout config and re-render sidebar item prices in the new currency.
 */
document.addEventListener("rs-currency-changed", (e) => {
  const newCurrency = e.detail.currency;
  if (window.B2B_CHECKOUT_CONFIG) {
    window.B2B_CHECKOUT_CONFIG.currency = newCurrency;
  }
});
